import React, { useState, useEffect, useMemo } from 'react';
import { sanitizeHtml } from '../lib/sanitize';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { 
  Plus, Search, FileText, MoreHorizontal, Eye, EyeOff, Edit, Trash2, Download, 
  CheckCircle2, Clock, Filter, Euro, Send, X,
  Sparkles, Zap, Calendar as CalendarIcon, Mail, Inbox, Package, BellRing, FolderOpen,
  AlertCircle, StickyNote, HandCoins, Pencil, Copy, Wrench
} from 'lucide-react';
import ProjectPhaseBadge from '../components/ProjectPhaseBadge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import PdfViewer from '../components/PdfViewer';
import CcSelector from '../components/CcSelector';
import EmailPreview, { EmailPreviewButton } from '../components/EmailPreview';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api, { factuurAPI, offerteAPI, customerAPI, propertyAPI, installationFactuurAPI, installationOfferteAPI, installationProductAPI, creditnotaAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePrivacy } from '../context/PrivacyContext';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import InstallationProductSelector from '../components/InstallationProductSelector';
import { SearchSelect } from '../components/SearchSelect';

const Facturen = () => {
  const [activeTab, setActiveTab] = useState('installatie');
  const [facturen, setFacturen] = useState([]);
  const [installationFacturen, setInstallationFacturen] = useState([]);
  const [installationProducts, setInstallationProducts] = useState([]);
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [acceptedOffertes, setAcceptedOffertes] = useState([]);
  const [acceptedInstallationOffertes, setAcceptedInstallationOffertes] = useState([]);
  const [phaseMap, setPhaseMap] = useState({});
  const [refDialog, setRefDialog] = useState(null); // {offerte, loading}
  useEffect(() => {
    api.get('/installation-offertes/project-phases').then(r => setPhaseMap(r.data || {})).catch(() => {});
  }, []);

  const openRefDialog = async (factuur) => {
    if (!factuur.offerte_id) return;
    setRefDialog({ loading: true, number: factuur.offerte_number });
    try {
      const [offRes, pdfRes] = await Promise.all([
        api.get(`/installation-offertes/${factuur.offerte_id}`),
        installationOfferteAPI.getPdf(factuur.offerte_id),
      ]);
      const blobUrl = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      setRefDialog({ offerte: offRes.data, pdfUrl: blobUrl, loading: false });
    } catch {
      toast.error('Kon offerte-PDF niet laden');
      setRefDialog(null);
    }
  };

  const closeRefDialog = () => {
    if (refDialog?.pdfUrl) window.URL.revokeObjectURL(refDialog.pdfUrl);
    setRefDialog(null);
  };

  const copyText = (text, label = 'Gekopieerd') => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success(label);
    } catch {
      toast.error('Kopiëren mislukt');
    }
  };
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [phaseFilter, setPhaseFilter] = useState('all');

  const offertePhaseStage = (offerteId) => {
    if (!offerteId) return null;
    return phaseMap[offerteId]?.stage || 'te_plannen';
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [installationDeleteConfirm, setInstallationDeleteConfirm] = useState(null);
  const [editingFactuur, setEditingFactuur] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedOfferte, setSelectedOfferte] = useState(null);
  
  // PDF Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFactuur, setPreviewFactuur] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [driveUrl, setDriveUrl] = useState(null);
  const [driveSaving, setDriveSaving] = useState(false);
  
  // Installation Factuur PDF Preview state
  const [installationPreviewOpen, setInstallationPreviewOpen] = useState(false);
  const [installationPreviewFactuur, setInstallationPreviewFactuur] = useState(null);
  const [installationPreviewUrl, setInstallationPreviewUrl] = useState(null);
  const [installationPreviewLoading, setInstallationPreviewLoading] = useState(false);
  
  // Installation Factuur Edit state
  const [editingInstallationFactuur, setEditingInstallationFactuur] = useState(null);
  const [installationEditItems, setInstallationEditItems] = useState([]);
  // Kommagetallen toestaan: "2,5" -> 2.5 (en "1.234,50" -> 1234.5)
  const parseDec = (v) => {
    let s = String(v ?? '');
    if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  };
  // Products/specifications from linked offerte (editable in same dialog)
  const [linkedOfferteItems, setLinkedOfferteItems] = useState([]);
  const [linkedOfferteId, setLinkedOfferteId] = useState(null);
  const [loadingLinkedOfferte, setLoadingLinkedOfferte] = useState(false);
  // Manual installation factuur creation (no offerte)
  const [manualFactuurDialogOpen, setManualFactuurDialogOpen] = useState(false);
  const [manualFactuurSaving, setManualFactuurSaving] = useState(false);
  const [manualFactuurForm, setManualFactuurForm] = useState({
    factuur_number: '',
    hide_unit_prices: false,
    use_manual_subtotal: false,
    manual_subtotal: 0,
    customer_id: '',
    property_id: '',
    customer_name_override: '',
    attn_override: '',
    billing_address: '',
    installation_address: '',
    language: 'nl',
    vat_percentage: 6,
    due_days: 8,
    notes: '',
    reference: '',
    items: [{ name: '', description: '', quantity: 1, unit_price: 0, unit: 'stuk', show_price: true, components: [] }],
  });
  
  // Email Queue Dialog state for installation facturen
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogFactuur, setEmailDialogFactuur] = useState(null);
  const [emailDialogCcEmails, setEmailDialogCcEmails] = useState([]);
  const [emailDialogSending, setEmailDialogSending] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  
  // Email preview state for hover functionality (like Offertes) - Installation
  const [emailPreviewData, setEmailPreviewData] = useState({});
  const [emailPreviewLoadingState, setEmailPreviewLoadingState] = useState({});
  const [emailSending, setEmailSending] = useState({});
  
  // Email preview state for reiniging facturen
  const [reinigingEmailPreviewData, setReinigingEmailPreviewData] = useState({});
  const [reinigingEmailPreviewLoadingState, setReinigingEmailPreviewLoadingState] = useState({});
  const [reinigingEmailSending, setReinigingEmailSending] = useState({});
  
  // Term Invoice Creation Dialog state
  const [termDialogOpen, setTermDialogOpen] = useState(false);
  const [selectedOfferteForTerm, setSelectedOfferteForTerm] = useState(null);
  const [termPercentages, setTermPercentages] = useState([
    { percentage: 40, description: 'Voorschot' },
    { percentage: 50, description: 'Na montage' },
    { percentage: 10, description: 'Eindfactuur' }
  ]);
  const [creatingTerm, setCreatingTerm] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);
  const saveTermsToOfferte = async () => {
    if (!selectedOfferteForTerm) return;
    const totalPct = termPercentages.reduce((sum, t) => sum + (t.percentage || 0), 0);
    if (totalPct !== 100 && !window.confirm(`De termijnen tellen op tot ${totalPct}% in plaats van 100%. Toch opslaan?`)) {
      return;
    }
    setSavingTerms(true);
    try {
      await installationOfferteAPI.update(selectedOfferteForTerm.id, { custom_payment_terms: termPercentages });
      toast.success('Termijnen opgeslagen op de offerte');
      fetchData();
      refreshReminders();
    } catch (err) {
      toast.error('Kon termijnen niet opslaan');
    } finally {
      setSavingTerms(false);
    }
  };
  
  // Show more state for accepted offertes sections
  const [showAllReinigingOffertes, setShowAllReinigingOffertes] = useState(false);
  const [showAllInstallationOffertes, setShowAllInstallationOffertes] = useState(false);
  const [expandedReminderTiles, setExpandedReminderTiles] = useState({});
  const [installOfferteSearch, setInstallOfferteSearch] = useState('');
  
  const { hasRole } = useAuth();
  const { privacy } = usePrivacy();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const defaultItem = { description: '', quantity: 1, unit_price: 0 };
  
  // Calculate default due date (8 days from today)
  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 8);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    offerte_id: '',
    factuur_number: '',
    factuur_date: new Date().toISOString().split('T')[0],
    due_date: getDefaultDueDate(),
    customer_id: '',
    property_id: '',
    language: 'nl',
    items: [
      { description: 'Onderhoud PV-installatie', quantity: 0, unit_price: 12.50 },
      { description: 'Reiniging en spoeling met osmosewater', quantity: 0, unit_price: 0 },
      { description: 'Algemeen', quantity: 0, unit_price: 0 },
      { description: 'Indienststelling en heropstart PV installatie na controle', quantity: 1, unit_price: 30.49 },
    ],
    vat_percentage: 6,
    discount: 0,
    notes: 'Wateraansluiting Gardena-koppeling noodzakelijk te voorzien door de klant.',
    reference: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Meldingen: welke facturen mogen uitgestuurd worden (o.b.v. getekende offertes + projectfases)
  const [factuurReminders, setFactuurReminders] = useState({ voorschot: [], na_montage: [], na_keuring: [] });
  const refreshReminders = () => {
    api.get('/installation-facturen/factuur-reminders')
      .then(res => setFactuurReminders(res.data || { voorschot: [], na_montage: [], na_keuring: [] }))
      .catch(() => {});
  };
  useEffect(() => { refreshReminders(); }, []);

  // Open de bewerk-dialoog voor een installatiefactuur (incl. gekoppelde offerte-items)
  const openInstallationFactuurEditor = async (factuur) => {
    setEditingInstallationFactuur(factuur);
    setInstallationEditItems(factuur.items?.map(item => ({
      ...item,
      show_price: item.show_price !== false
    })) || []);
    setLinkedOfferteItems([]);
    setLinkedOfferteId(null);
    if (factuur.offerte_id) {
      setLoadingLinkedOfferte(true);
      try {
        const resp = await installationOfferteAPI.getOne(factuur.offerte_id);
        const offerte = resp.data;
        setLinkedOfferteId(offerte.id);
        setLinkedOfferteItems((offerte.items || []).map(it => ({
          ...it,
          components: it.components || [],
          sub_products: it.sub_products || [],
        })));
      } catch (err) {
        console.error('Kon offerte niet laden:', err);
      } finally {
        setLoadingLinkedOfferte(false);
      }
    }
  };

  // Melding verbergen (bv. al extern gefactureerd of afwijkende termijnen)
  const handleDismissReminder = async (tileKey, rem) => {
    try {
      await api.put(`/installation-offertes/${rem.offerte_id}/dismiss-reminder`, { reminder: tileKey });
      setFactuurReminders(prev => ({ ...prev, [tileKey]: (prev[tileKey] || []).filter(r => r.offerte_id !== rem.offerte_id) }));
      toast.success('Melding verborgen', {
        action: {
          label: 'Ongedaan maken',
          onClick: async () => {
            try {
              await api.put(`/installation-offertes/${rem.offerte_id}/dismiss-reminder`, { reminder: tileKey, dismissed: false });
              refreshReminders();
              toast.success('Melding teruggezet');
            } catch (e) {
              toast.error('Kon melding niet terugzetten');
            }
          }
        },
        duration: 6000
      });
    } catch (err) {
      toast.error('Kon melding niet verbergen');
    }
  };

  // Klik op meldingsitem: maak conceptfactuur voor de termijn aan (of open bestaande) en open de editor
  const handleReminderClick = async (tileKey, rem) => {
    const term = { voorschot: 1, na_montage: 2, na_keuring: 3 }[tileKey];
    const existing = installationFacturen.find(f => f.offerte_id === rem.offerte_id && f.term_number === term);
    if (existing) {
      openInstallationFactuurEditor(existing);
      return;
    }
    try {
      const res = await installationFactuurAPI.createTermFromOfferte(rem.offerte_id, term);
      toast.success(`Conceptfactuur ${res.data.factuur_number} aangemaakt — je kan hem nu bewerken`);
      fetchData();
      refreshReminders();
      openInstallationFactuurEditor(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon factuur niet aanmaken');
    }
  };

  // Handle URL params for creating factuur from offerte
  useEffect(() => {
    const offerteId = searchParams.get('offerte_id');
    if (offerteId && acceptedOffertes.length > 0 && !loading) {
      const offerte = acceptedOffertes.find(o => o.id === offerteId);
      if (offerte) {
        handleCreateFromOfferte(offerte);
        setSearchParams({});
      }
    }
  }, [searchParams, acceptedOffertes, loading]);

  const fetchData = async () => {
    try {
      const [facturenRes, offertesRes, customersRes, propertiesRes, instFacturenRes, instOffertesRes, instProductsRes, creditnotasRes] = await Promise.all([
        factuurAPI.getAll(),
        offerteAPI.getAll(),
        customerAPI.getAll(),
        propertyAPI.getAll(),
        installationFactuurAPI.getAll(),
        installationOfferteAPI.getAll(),
        installationProductAPI.getAll(),
        creditnotaAPI.getAll().catch(() => ({ data: [] }))
      ]);
      
      setFacturen(facturenRes.data || []);
      setInstallationFacturen(instFacturenRes.data || []);
      setInstallationProducts(instProductsRes.data || []);
      setCreditnotas(creditnotasRes.data || []);
      // Filter: alleen GEACCEPTEERDE offertes zonder factuur (getekend = klaar voor facturatie)
      const accepted = (offertesRes.data || []).filter(o => 
        o.status === 'accepted' || o.status === 'geaccepteerd'
      );
      setAcceptedOffertes(accepted);
      
      // Filter for accepted installation offertes that are not fully paid
      const instFacturen = instFacturenRes.data || [];
      const acceptedInst = (instOffertesRes.data || []).filter(o => {
        // Must be accepted
        if (o.status !== 'accepted' && o.status !== 'geaccepteerd') return false;
        
        // Check if all invoices for this offerte are paid
        const offerteFacturen = instFacturen.filter(f => 
          f.offerte_id === o.id || f.reference === o.offerte_number
        );
        
        // If no invoices yet, show it
        if (offerteFacturen.length === 0) return true;
        
        // Check if there's a full invoice (100%) that's paid
        const fullInvoice = offerteFacturen.find(f => !f.term_number && f.status === 'paid');
        if (fullInvoice) return false; // Fully invoiced and paid
        
        // Check if all term invoices are paid
        const termInvoices = offerteFacturen.filter(f => f.term_number);
        if (termInvoices.length >= 3) {
          const allPaid = termInvoices.every(f => f.status === 'paid');
          if (allPaid) return false; // All terms paid
        }
        
        // Check if total invoiced equals or exceeds offerte total and all paid
        const totalInvoiced = offerteFacturen.reduce((sum, f) => sum + (f.total_incl_vat || 0), 0);
        const allInvoicesPaid = offerteFacturen.every(f => f.status === 'paid');
        if (allInvoicesPaid && totalInvoiced >= (o.total_incl_vat * 0.99)) {
          return false; // Fully invoiced and paid (with 1% tolerance for rounding)
        }
        
        return true;
      });
      setAcceptedInstallationOffertes(acceptedInst);
      setCustomers(customersRes.data || []);
      setProperties(propertiesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Kon data niet laden');
    } finally {
      setLoading(false);
    }
  };

  // Open term dialog for creating term-based invoices
  const openTermDialog = (offerte) => {
    setSelectedOfferteForTerm(offerte);
    // Use custom_payment_terms from offerte if available, otherwise use defaults
    if (offerte.custom_payment_terms && offerte.custom_payment_terms.length >= 2) {
      setTermPercentages(offerte.custom_payment_terms);
    } else {
      setTermPercentages([
        { percentage: 40, description: 'Voorschot' },
        { percentage: 50, description: 'Na montage' },
        { percentage: 10, description: 'Eindfactuur' }
      ]);
    }
    setTermDialogOpen(true);
  };

  // Create term invoice
  const handleCreateTermInvoice = async (termNumber, percentage) => {
    if (!selectedOfferteForTerm) return;
    setCreatingTerm(true);
    try {
      await installationFactuurAPI.createTermFromOfferte(
        selectedOfferteForTerm.id, 
        termNumber, 
        percentage
      );
      toast.success(`Factuur termijn ${termNumber} (${percentage}%) aangemaakt`);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Kon factuur niet aanmaken';
      toast.error(message);
    } finally {
      setCreatingTerm(false);
    }
  };

  const handleCreateFromOfferte = (offerte) => {
    const customer = customers.find(c => c.id === offerte.customer_id);
    const property = properties.find(p => p.id === offerte.property_id);
    
    // Convert offerte items to factuur items
    const items = offerte.items?.map(item => ({
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price
    })) || [];
    
    // Generate reference from offerte
    const reference = `${offerte.offerte_number} ${new Date().toLocaleDateString('nl-BE').replace(/\//g, '')}`;
    
    setFormData({
      offerte_id: offerte.id,
      factuur_number: '', // Will be auto-generated
      factuur_date: new Date().toISOString().split('T')[0],
      due_date: getDefaultDueDate(),
      customer_id: offerte.customer_id,
      property_id: offerte.property_id || '',
      language: offerte.language || 'nl',
      items: items.length > 0 ? items : [defaultItem],
      vat_percentage: offerte.vat_percentage || 6,
      discount: offerte.discount || 0,
      notes: offerte.notes || 'Wateraansluiting Gardena-koppeling noodzakelijk te voorzien door de klant.',
      reference: reference
    });
    setSelectedOfferte(offerte);
    setEditingFactuur(null);
    setDialogOpen(true);
  };

  const handleEdit = (factuur) => {
    setFormData({
      offerte_id: factuur.offerte_id || '',
      factuur_number: factuur.factuur_number,
      factuur_date: factuur.factuur_date,
      due_date: factuur.due_date,
      customer_id: factuur.customer_id,
      property_id: factuur.property_id || '',
      language: factuur.language || 'nl',
      items: factuur.items?.map(i => ({
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price
      })) || [defaultItem],
      vat_percentage: factuur.vat_percentage,
      discount: factuur.discount || 0,
      notes: factuur.notes || '',
      reference: factuur.reference || ''
    });
    setEditingFactuur(factuur);
    setSelectedOfferte(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Selecteer een klant');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        discount: parseDec(formData.discount),
        items: formData.items.map(it => ({ ...it, quantity: parseDec(it.quantity), unit_price: parseDec(it.unit_price) })),
      };
      if (editingFactuur) {
        await factuurAPI.update(editingFactuur.id, payload);
        toast.success('Factuur bijgewerkt');
      } else {
        const response = await factuurAPI.create(payload);
        toast.success('Factuur aangemaakt');
        
        // Open PDF preview
        if (response.data?.id) {
          handlePreview({ id: response.data.id, factuur_number: response.data.factuur_number });
        }
      }
      setDialogOpen(false);
      setEditingFactuur(null);
      setSelectedOfferte(null);
      fetchData();
      resetForm();
    } catch (error) {
      toast.error('Kon factuur niet opslaan');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      offerte_id: '',
      factuur_number: '',
      factuur_date: new Date().toISOString().split('T')[0],
      due_date: getDefaultDueDate(),
      customer_id: '',
      property_id: '',
      items: [defaultItem],
      vat_percentage: 6,
      discount: 0,
      notes: 'Wateraansluiting Gardena-koppeling noodzakelijk te voorzien door de klant.',
      reference: ''
    });
  };

  const handleDelete = async (id) => {
    try {
      await factuurAPI.delete(id);
      toast.success('Factuur verwijderd');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Kon factuur niet verwijderen');
    }
  };

  const handleInstallationDelete = async (id) => {
    try {
      await installationFactuurAPI.delete(id);
      toast.success('Factuur verwijderd');
      setInstallationDeleteConfirm(null);
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Kon factuur niet verwijderen');
    }
  };

  const handlePreview = async (factuur) => {
    setPreviewFactuur(factuur);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);
    setDriveUrl(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/facturen/${factuur.id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Could not load PDF');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewUrl(blobUrl);
      setPreviewLoading(false);
    } catch (error) {
      toast.error('Kon PDF niet laden');
      console.error(error);
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewOpen(false);
    setPreviewUrl(null);
    setPreviewFactuur(null);
    setDriveUrl(null);
  };

  // Direct PDF download for reiniging facturen (like offertes)
  const handlePushFactuurToSharepoint = async (factuur, isInstallation = false) => {
    try {
      toast.loading('Naar klantmap pushen...', { id: 'sp-push' });
      await api.post(`/${isInstallation ? 'installation-facturen' : 'facturen'}/${factuur.id}/send-to-sharepoint`);
      toast.success('Factuur naar klantmap gepusht', { id: 'sp-push' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon factuur niet naar klantmap pushen', { id: 'sp-push' });
    }
  };

  const formatFactuurFilename = (factuur) => {
    const customerName = factuur.customer_name || '';
    const factuurNumber = (factuur.factuur_number || factuur.id).replace(/\//g, '-');
    const d = factuur.factuur_date ? new Date(factuur.factuur_date) : new Date();
    const datum = `${String(d.getDate()).padStart(2, '0')}${String(d.getMonth() + 1).padStart(2, '0')}${d.getFullYear()}`;
    return `${factuurNumber} ${customerName} ${datum}.pdf`;
  };

  const handleDirectDownload = async (factuur) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/facturen/${factuur.id}/pdf`;
    
    try {
      toast.loading('PDF downloaden...', { id: 'pdf-download' });
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed');
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = blobUrl;
      // Bestandsnaam: [factuurnummer] [klantnaam] [factuurdatum].pdf
      a.download = formatFactuurFilename(factuur);
      a.click();
      
      URL.revokeObjectURL(blobUrl);
      toast.success('PDF gedownload', { id: 'pdf-download' });
    } catch (err) {
      console.error(err);
      toast.error('Download mislukt. Probeer opnieuw.', { id: 'pdf-download' });
    }
  };

  // Direct PDF download for installation facturen
  const handleInstallationDirectDownload = async (factuur) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/installation-facturen/${factuur.id}/pdf`;
    
    try {
      toast.loading('PDF downloaden...', { id: 'pdf-download' });
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed');
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = blobUrl;
      // Bestandsnaam: [factuurnummer] [klantnaam] [factuurdatum].pdf
      a.download = formatFactuurFilename(factuur);
      a.click();
      
      URL.revokeObjectURL(blobUrl);
      toast.success('PDF gedownload', { id: 'pdf-download' });
    } catch (err) {
      console.error(err);
      toast.error('Download mislukt. Probeer opnieuw.', { id: 'pdf-download' });
    }
  };

  // Installation Factuur preview functions
  const handleInstallationPreview = async (factuur) => {
    setInstallationPreviewFactuur(factuur);
    setInstallationPreviewOpen(true);
    setInstallationPreviewLoading(true);
    setInstallationPreviewUrl(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/installation-facturen/${factuur.id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Could not load PDF');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setInstallationPreviewUrl(blobUrl);
      setInstallationPreviewLoading(false);
    } catch (error) {
      toast.error('Kon PDF niet laden');
      console.error(error);
      setInstallationPreviewLoading(false);
    }
  };

  const closeInstallationPreview = () => {
    if (installationPreviewUrl) {
      window.URL.revokeObjectURL(installationPreviewUrl);
    }
    setInstallationPreviewOpen(false);
    setInstallationPreviewUrl(null);
    setInstallationPreviewFactuur(null);
  };

  // Fetch email preview for hover functionality (like Offertes)
  const fetchEmailPreview = async (factuurId) => {
    if (emailPreviewData[factuurId]) return; // Already fetched
    
    setEmailPreviewLoadingState(prev => ({ ...prev, [factuurId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/installation-facturen/${factuurId}/email-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const preview = await response.json();
        setEmailPreviewData(prev => ({ ...prev, [factuurId]: preview }));
      }
    } catch (error) {
      console.error('Error fetching email preview:', error);
    } finally {
      setEmailPreviewLoadingState(prev => ({ ...prev, [factuurId]: false }));
    }
  };

  // Queue email for installation factuur (for EmailPreviewButton)
  const handleQueueFactuurEmail = async (factuur) => {
    const customer = customers.find(c => c.id === factuur.customer_id);
    const property = properties.find(p => p.id === factuur.property_id);
    
    if (!customer?.email) {
      toast.error('Klant heeft geen email adres');
      return;
    }
    
    setEmailSending(prev => ({ ...prev, [factuur.id]: true }));
    
    try {
      const webhookData = {
        factuur_id: factuur.id,
        customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
        customer_email: customer.email,
        property_address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
        cc_emails: []
      };
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/installatie-factuur-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(webhookData)
      });
      
      if (!response.ok) throw new Error('Failed to queue email');
      
      toast.success('Email klaargezet in Mails > Klaargezet');
      await installationFactuurAPI.updateStatus(factuur.id, 'sent');
      fetchData();
    } catch (error) {
      console.error('Error queueing email:', error);
      toast.error('Kon email niet klaarzetten');
    } finally {
      setEmailSending(prev => ({ ...prev, [factuur.id]: false }));
    }
  };

  // Send email directly for installation factuur (for EmailPreviewButton)
  const handleSendFactuurEmailDirect = async (factuur) => {
    const customer = customers.find(c => c.id === factuur.customer_id);
    const property = properties.find(p => p.id === factuur.property_id);
    
    if (!customer?.email) {
      toast.error('Klant heeft geen email adres');
      return;
    }
    
    setEmailSending(prev => ({ ...prev, [factuur.id]: true }));
    
    try {
      const webhookData = {
        factuur_id: factuur.id,
        customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
        customer_email: customer.email,
        property_address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
        cc_emails: []
      };
      
      const token = localStorage.getItem('token');
      
      // Queue first
      const queueResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/installatie-factuur-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(webhookData)
      });
      
      if (!queueResponse.ok) throw new Error('Failed to queue email');
      
      const queueData = await queueResponse.json();
      const emailId = queueData.email_id;
      
      // Send immediately
      const sendResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email-queue/${emailId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (sendResponse.ok) {
        toast.success('Email verzonden!');
      } else {
        toast.success('Email klaargezet in wachtrij');
      }
      
      await installationFactuurAPI.updateStatus(factuur.id, 'sent');
      fetchData();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Kon email niet verzenden');
    } finally {
      setEmailSending(prev => ({ ...prev, [factuur.id]: false }));
    }
  };

  // === REINIGING FACTUUR EMAIL FUNCTIONS ===
  
  // Fetch email preview for reiniging facturen
  const fetchReinigingEmailPreview = async (factuurId) => {
    if (reinigingEmailPreviewData[factuurId]) return;
    
    setReinigingEmailPreviewLoadingState(prev => ({ ...prev, [factuurId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/facturen/${factuurId}/email-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const preview = await response.json();
        setReinigingEmailPreviewData(prev => ({ ...prev, [factuurId]: preview }));
      }
    } catch (error) {
      console.error('Error fetching reiniging email preview:', error);
    } finally {
      setReinigingEmailPreviewLoadingState(prev => ({ ...prev, [factuurId]: false }));
    }
  };

  // Queue email for reiniging factuur
  const handleQueueReinigingFactuurEmail = async (factuur) => {
    const customer = customers.find(c => c.id === factuur.customer_id);
    const property = properties.find(p => p.id === factuur.property_id);
    
    if (!customer?.email) {
      toast.error('Klant heeft geen email adres');
      return;
    }
    
    setReinigingEmailSending(prev => ({ ...prev, [factuur.id]: true }));
    
    try {
      const webhookData = {
        factuur_id: factuur.id,
        customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
        customer_email: customer.email,
        property_address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
        cc_emails: []
      };
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/reiniging-factuur-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(webhookData)
      });
      
      if (!response.ok) throw new Error('Failed to queue email');
      
      toast.success('Email klaargezet in Mails > Klaargezet');
      await factuurAPI.updateStatus(factuur.id, 'sent');
      fetchData();
    } catch (error) {
      console.error('Error queueing reiniging email:', error);
      toast.error('Kon email niet klaarzetten');
    } finally {
      setReinigingEmailSending(prev => ({ ...prev, [factuur.id]: false }));
    }
  };

  // Send reiniging factuur email directly
  const handleSendReinigingFactuurEmailDirect = async (factuur) => {
    const customer = customers.find(c => c.id === factuur.customer_id);
    const property = properties.find(p => p.id === factuur.property_id);
    
    if (!customer?.email) {
      toast.error('Klant heeft geen email adres');
      return;
    }
    
    setReinigingEmailSending(prev => ({ ...prev, [factuur.id]: true }));
    
    try {
      const webhookData = {
        factuur_id: factuur.id,
        customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
        customer_email: customer.email,
        property_address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
        cc_emails: []
      };
      
      const token = localStorage.getItem('token');
      
      // Queue first
      const queueResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/reiniging-factuur-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(webhookData)
      });
      
      if (!queueResponse.ok) throw new Error('Failed to queue email');
      
      const queueData = await queueResponse.json();
      const emailId = queueData.email_id;
      
      // Send immediately
      const sendResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email-queue/${emailId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (sendResponse.ok) {
        toast.success('Email verzonden!');
      } else {
        toast.success('Email klaargezet in wachtrij');
      }
      
      await factuurAPI.updateStatus(factuur.id, 'sent');
      fetchData();
    } catch (error) {
      console.error('Error sending reiniging email:', error);
      toast.error('Kon email niet verzenden');
    } finally {
      setReinigingEmailSending(prev => ({ ...prev, [factuur.id]: false }));
    }
  };

  // Open email dialog for installation factuur
  const openEmailDialog = async (factuur) => {
    setEmailDialogFactuur(factuur);
    setEmailDialogCcEmails([]);
    setEmailDialogOpen(true);
    setEmailPreview(null);
    setEmailPreviewLoading(true);
    
    // Load email preview
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/installation-facturen/${factuur.id}/email-preview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const preview = await response.json();
        setEmailPreview(preview);
        if (Array.isArray(preview.cc_emails)) setEmailDialogCcEmails(preview.cc_emails);
      }
    } catch (error) {
      console.error('Error loading email preview:', error);
    } finally {
      setEmailPreviewLoading(false);
    }
  };

  // Queue installation factuur email
  const handleQueueInstallationFactuurEmail = async () => {
    if (!emailDialogFactuur) return;
    
    setEmailDialogSending(true);
    try {
      const customer = customers.find(c => c.id === emailDialogFactuur.customer_id);
      const property = properties.find(p => p.id === emailDialogFactuur.property_id);
      
      if (!customer?.email) {
        toast.error('Klant heeft geen email adres');
        return;
      }
      
      const webhookData = {
        factuur_id: emailDialogFactuur.id,
        customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
        customer_email: customer.email,
        property_address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
        cc_emails: emailDialogCcEmails
      };
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/installatie-factuur-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(webhookData)
      });
      
      if (!response.ok) throw new Error('Failed to queue email');
      
      const data = await response.json();
      
      toast.success('Email klaargezet in Mails > Klaargezet');
      setEmailDialogOpen(false);
      setEmailDialogFactuur(null);
      setEmailDialogCcEmails([]);
      
      // Update factuur status to 'sent'
      await installationFactuurAPI.updateStatus(emailDialogFactuur.id, 'sent');
      fetchData();
    } catch (error) {
      console.error('Error queueing email:', error);
      toast.error('Kon email niet klaarzetten');
    } finally {
      setEmailDialogSending(false);
    }
  };

  // Send installation factuur email directly
  const handleSendInstallationFactuurEmailDirect = async () => {
    if (!emailDialogFactuur) return;
    
    setEmailDialogSending(true);
    try {
      const customer = customers.find(c => c.id === emailDialogFactuur.customer_id);
      const property = properties.find(p => p.id === emailDialogFactuur.property_id);
      
      if (!customer?.email) {
        toast.error('Klant heeft geen email adres');
        return;
      }
      
      const webhookData = {
        factuur_id: emailDialogFactuur.id,
        customer_name: `${customer.first_name} ${customer.last_name}`.trim(),
        customer_email: customer.email,
        property_address: property ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}` : '',
        cc_emails: emailDialogCcEmails
      };
      
      const token = localStorage.getItem('token');
      
      // First queue the email
      const queueResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webhook/installatie-factuur-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(webhookData)
      });
      
      if (!queueResponse.ok) throw new Error('Failed to queue email');
      
      const queueData = await queueResponse.json();
      const emailId = queueData.email_id;
      
      // Then send it immediately
      const sendResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email-queue/${emailId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (sendResponse.ok) {
        toast.success('Email verzonden!');
      } else {
        toast.success('Email klaargezet in wachtrij');
      }
      
      setEmailDialogOpen(false);
      setEmailDialogFactuur(null);
      setEmailDialogCcEmails([]);
      
      // Update factuur status to 'sent'
      await installationFactuurAPI.updateStatus(emailDialogFactuur.id, 'sent');
      fetchData();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Kon email niet verzenden');
    } finally {
      setEmailDialogSending(false);
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...defaultItem }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => 
      sum + (parseDec(item.quantity) * parseDec(item.unit_price)), 0
    );
    const discountAmount = parseDec(formData.discount);
    const totalExcl = subtotal - discountAmount;
    const vatAmount = totalExcl * (formData.vat_percentage / 100);
    const total = totalExcl + vatAmount;
    return { subtotal, discountAmount, totalExcl, vatAmount, total };
  };

  const { subtotal, discountAmount, totalExcl, vatAmount, total } = calculateTotals();

  const effStatus = (f) => {
    if (f.status === 'sent' && f.due_date && String(f.due_date).slice(0, 10) < new Date().toISOString().slice(0, 10)) return 'overdue';
    return f.status;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Concept</Badge>;
      case 'sent':
        return <Badge className="bg-blue-500/20 text-blue-400">Verzonden</Badge>;
      case 'paid':
        return <Badge className="bg-green-500/20 text-green-400">Betaald</Badge>;
      case 'overdue':
        return <Badge className="bg-red-500/20 text-red-400">Te laat</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const [allSortBy, setAllSortBy] = useState('nummer');

  const filteredFacturen = useMemo(() => {
    const list = facturen.filter(f => {
      const matchesSearch = search === '' || 
        f.factuur_number?.toLowerCase().includes(search.toLowerCase()) ||
        f.customer_name?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || effStatus(f) === statusFilter;
      return matchesSearch && matchesStatus;
    });
    const sorted = [...list];
    if (allSortBy === 'nummer') {
      sorted.sort((a, b) => String(b.factuur_number || '').localeCompare(String(a.factuur_number || ''), undefined, { numeric: true }));
    } else {
      sorted.sort((a, b) => String(b.factuur_date || b.created_at || '').localeCompare(String(a.factuur_date || a.created_at || '')));
    }
    return sorted;
  }, [facturen, search, statusFilter, allSortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Alle facturen (installatie + reiniging) gecombineerd
  const sortInvoices = (list, by) => {
    const sorted = [...list];
    if (by === 'nummer') {
      sorted.sort((a, b) => String(b.factuur_number || '').localeCompare(String(a.factuur_number || ''), undefined, { numeric: true }));
    } else {
      sorted.sort((a, b) => String(b.factuur_date || b.created_at || '').localeCompare(String(a.factuur_date || a.created_at || '')));
    }
    return sorted;
  };

  const outstanding = (f) => {
    if (f.status === 'paid') return 0;
    return Math.max((f.total_incl_vat || 0) - (f.amount_paid || 0), 0);
  };

  // Betaling registreren + interne notitie
  const [paymentDialog, setPaymentDialog] = useState(null); // {factuur, type}
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: '', note: '' });
  const [noteDialog, setNoteDialog] = useState(null); // {factuur, type}
  const [noteText, setNoteText] = useState('');

  // Creditnota's
  const [creditnotas, setCreditnotas] = useState([]);
  const [creditDialog, setCreditDialog] = useState(null); // {factuur, type}
  const [creditForm, setCreditForm] = useState({ amount_excl: '', description: '', creditnota_number: '' });
  const [editCnDialog, setEditCnDialog] = useState(null); // {id, creditnota_number}

  const openCreditDialog = (factuur, type) => {
    setCreditForm({
      amount_excl: String(factuur.total_excl_vat ?? factuur.subtotal ?? ''),
      description: `Creditering factuur ${factuur.factuur_number || ''}`.trim(),
      creditnota_number: '',
    });
    setCreditDialog({ factuur, type });
  };

  const saveCreditnota = async () => {
    const amount = parseDec(creditForm.amount_excl);
    if (!amount || amount <= 0) { toast.error('Geef een geldig bedrag in'); return; }
    try {
      const r = await creditnotaAPI.create({
        source_factuur_id: creditDialog.factuur.id,
        source_type: creditDialog.type,
        amount_excl: amount,
        description: creditForm.description,
        creditnota_number: creditForm.creditnota_number?.trim() || null,
      });
      toast.success(`Creditnota ${r.data.creditnota_number} aangemaakt`);
      setCreditDialog(null);
      setActiveTab('creditnotas');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Creditnota aanmaken mislukt'); }
  };

  const downloadCreditnotaPdf = async (cn) => {
    try {
      const r = await creditnotaAPI.downloadPdf(cn.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${cn.creditnota_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF downloaden mislukt'); }
  };

  const deleteCreditnota = async (cn) => {
    if (!window.confirm(`Creditnota ${cn.creditnota_number} verwijderen?`)) return;
    try {
      await creditnotaAPI.delete(cn.id);
      toast.success('Creditnota verwijderd');
      fetchData();
    } catch { toast.error('Verwijderen mislukt'); }
  };

  const openPaymentDialog = (factuur, type) => {
    setPaymentForm({ amount: '', date: new Date().toISOString().slice(0, 10), note: '' });
    setPaymentDialog({ factuur, type });
  };
  const openNoteDialog = (factuur, type) => {
    setNoteText(factuur.internal_note || '');
    setNoteDialog({ factuur, type });
  };
  const paymentApi = (type) => (type === 'installatie' ? installationFactuurAPI : factuurAPI);

  const savePayment = async () => {
    const amount = parseFloat(String(paymentForm.amount).replace(',', '.'));
    if (!amount || amount <= 0) { toast.error('Geef een geldig bedrag in'); return; }
    try {
      const r = await paymentApi(paymentDialog.type).addPayment(paymentDialog.factuur.id, {
        amount, date: paymentForm.date, note: paymentForm.note });
      const saldo = r.data.outstanding;
      toast.success(saldo <= 0 ? 'Betaling geregistreerd — factuur volledig betaald!' : `Betaling geregistreerd — nog ${formatCurrency(saldo)} openstaand`);
      setPaymentDialog(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Betaling registreren mislukt'); }
  };

  const removePayment = async (paymentId) => {
    try {
      await paymentApi(paymentDialog.type).deletePayment(paymentDialog.factuur.id, paymentId);
      toast.success('Betaling verwijderd');
      const fresh = { ...paymentDialog.factuur };
      fresh.payments = (fresh.payments || []).filter(p => p.id !== paymentId);
      fresh.amount_paid = fresh.payments.reduce((s, p) => s + p.amount, 0);
      setPaymentDialog({ ...paymentDialog, factuur: fresh });
      fetchData();
    } catch { toast.error('Verwijderen mislukt'); }
  };

  const saveNote = async () => {
    try {
      await paymentApi(noteDialog.type).updateInternalNote(noteDialog.factuur.id, noteText);
      toast.success('Notitie opgeslagen');
      setNoteDialog(null);
      fetchData();
    } catch { toast.error('Notitie opslaan mislukt'); }
  };

  const filteredInstallationFacturen = useMemo(() => {
    const list = installationFacturen.filter(f => {
      const matchesSearch = !search ||
        f.factuur_number?.toLowerCase().includes(search.toLowerCase()) ||
        f.customer_name?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || effStatus(f) === statusFilter;
      const matchesPhase = phaseFilter === 'all' || offertePhaseStage(f.offerte_id) === phaseFilter;
      return matchesSearch && matchesStatus && matchesPhase;
    });
    return sortInvoices(list, allSortBy);
  }, [installationFacturen, search, statusFilter, phaseFilter, phaseMap, allSortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const allInvoices = useMemo(() => {
    const clean = facturen.map(f => ({ ...f, _type: 'reiniging', _date: f.factuur_date || f.created_at || '' }));
    const inst = installationFacturen.map(f => ({ ...f, _type: 'installatie', _date: f.factuur_date || f.created_at || '' }));
    const all = [...clean, ...inst];
    if (allSortBy === 'nummer') {
      all.sort((a, b) => String(b.factuur_number || '').localeCompare(String(a.factuur_number || ''), undefined, { numeric: true }));
    } else {
      all.sort((a, b) => String(b._date).localeCompare(String(a._date)));
    }
    return all;
  }, [facturen, installationFacturen, allSortBy]);

  const formatCurrency = (value) => {
    if (privacy) return '€ \u2022\u2022\u2022\u2022';
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 max-w-full overflow-x-hidden px-2 sm:px-0" data-testid="facturen-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Facturen</h1>
          <p className="text-sm text-muted-foreground">Beheer facturen voor reinigingen en installaties</p>
        </div>
      </div>

      {/* Tabs for Reiniging / Installatie */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex">
            <TabsTrigger value="installatie" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="tab-installatie">
              <Zap className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Installatie</span>
              <span className="sm:hidden">Inst.</span>
              ({installationFacturen.length})
            </TabsTrigger>
            <TabsTrigger value="reiniging" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="tab-reiniging">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Reiniging</span>
              <span className="sm:hidden">Rein.</span>
              ({facturen.length})
            </TabsTrigger>
            <TabsTrigger value="interventies" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="tab-interventies">
              <Wrench className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Interventies</span>
              <span className="sm:hidden">Interv.</span>
              ({installationFacturen.filter(f => f.is_interventie).length})
            </TabsTrigger>
            <TabsTrigger value="alles" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="tab-alles">
              <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
              Alles ({facturen.length + installationFacturen.length})
            </TabsTrigger>
            <TabsTrigger value="creditnotas" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="tab-creditnotas">
              <HandCoins className="h-3 w-3 sm:h-4 sm:w-4" />
              Creditnota's ({creditnotas.length})
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Reiniging Facturen Tab */}
        <TabsContent value="reiniging" className="mt-4 space-y-6">

      {/* Accepted Offertes Section */}
      {acceptedOffertes.length > 0 && (
        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Getekende Offertes - Klaar voor facturatie ({acceptedOffertes.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(showAllReinigingOffertes ? acceptedOffertes : acceptedOffertes.slice(0, 6)).map(offerte => (
                <div 
                  key={offerte.id}
                  className="p-3 bg-background rounded-lg border flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium">{offerte.offerte_number}</p>
                    <p className="text-sm text-muted-foreground">{offerte.customer_name}</p>
                    <p className="text-sm font-semibold">{formatCurrency(offerte.total_incl_vat)}</p>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => handleCreateFromOfferte(offerte)}
                    data-testid={`create-factuur-${offerte.id}`}
                  >
                    <Euro className="w-4 h-4 mr-1" />
                    Factuur maken
                  </Button>
                </div>
              ))}
            </div>
            {acceptedOffertes.length > 6 && (
              <div className="mt-3 text-center">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAllReinigingOffertes(!showAllReinigingOffertes)}
                >
                  {showAllReinigingOffertes 
                    ? 'Minder tonen' 
                    : `Meer bekijken (+${acceptedOffertes.length - 6})`}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Zoek op factuurnummer of klant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="factuur-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="factuur-status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="draft">Concept</SelectItem>
            <SelectItem value="sent">Verzonden</SelectItem>
            <SelectItem value="paid">Betaald</SelectItem>
            <SelectItem value="overdue">Te laat</SelectItem>
          </SelectContent>
        </Select>
        <Select value={allSortBy} onValueChange={setAllSortBy}>
          <SelectTrigger className="w-[180px]" data-testid="reiniging-sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nummer">Sorteer: Factuurnummer</SelectItem>
            <SelectItem value="datum">Sorteer: Factuurdatum</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Facturen Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Totaal</p>
            <p className="text-2xl font-bold" data-testid="kpi-reiniging-totaal">{facturen.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Concept</p>
            <p className="text-2xl font-bold" data-testid="kpi-reiniging-concept">{facturen.filter(f => f.status === 'draft').length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Betaald</p>
            <p className="text-2xl font-bold text-green-500" data-testid="kpi-reiniging-betaald">{facturen.filter(f => f.status === 'paid').length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Openstaand</p>
            <p className="text-2xl font-bold text-amber-500" data-testid="kpi-reiniging-openstaand">
              {formatCurrency(facturen.filter(f => ['sent', 'overdue'].includes(effStatus(f))).reduce((sum, f) => sum + (f.total_incl_vat || 0), 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Facturen Table */}
      <Card>
        <CardContent className="p-0 overflow-hidden">
          <div className="overflow-x-auto -mx-px">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 sm:p-4 font-medium text-xs sm:text-sm">Factuurnr</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-xs sm:text-sm">Klant</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-xs sm:text-sm hidden sm:table-cell">Datum</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-xs sm:text-sm">Bedrag</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-xs sm:text-sm">Openstaand</th>
                  <th className="text-left p-3 sm:p-4 font-medium text-xs sm:text-sm">Status</th>
                  <th className="text-right p-3 sm:p-4 font-medium text-xs sm:text-sm">Acties</th>
                </tr>
              </thead>
              <tbody>
                {filteredFacturen.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Geen facturen gevonden</p>
                      <p className="text-sm">Maak een factuur aan van een getekende offerte</p>
                    </td>
                  </tr>
                ) : (
                  filteredFacturen.map(factuur => (
                    <tr key={factuur.id} className="border-b hover:bg-muted/50" data-testid={`reiniging-factuur-row-${factuur.id}`}>
                      <td className="p-4">
                        <span className="font-mono font-medium">{factuur.factuur_number}</span>
                        {factuur.internal_note && (
                          <button onClick={() => openNoteDialog(factuur, 'reiniging')} title={factuur.internal_note} data-testid={`note-indicator-${factuur.id}`}>
                            <AlertCircle className="w-4 h-4 inline ml-1.5 text-amber-500" />
                          </button>
                        )}
                      </td>
                      <td className="p-4">
                        <p className="font-medium">{factuur.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{factuur.property_address || factuur.customer_address}</p>
                      </td>
                      <td className="p-4">
                        <p>{new Date(factuur.factuur_date).toLocaleDateString('nl-BE')}</p>
                        <p className="text-sm text-muted-foreground">
                          Verval: {new Date(factuur.due_date).toLocaleDateString('nl-BE')}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="font-semibold">{formatCurrency(factuur.total_incl_vat)}</p>
                        <p className="text-sm text-muted-foreground">excl. {formatCurrency(factuur.total_excl_vat)}</p>
                      </td>
                      <td className="p-4">
                        {outstanding(factuur) > 0 ? (
                          <div>
                            <p className="font-semibold text-amber-500" data-testid={`outstanding-${factuur.id}`}>{formatCurrency(outstanding(factuur))}</p>
                            {(factuur.amount_paid || 0) > 0 && <p className="text-xs text-muted-foreground">{formatCurrency(factuur.amount_paid)} ontvangen</p>}
                          </div>
                        ) : (
                          <p className="font-semibold text-green-500">{formatCurrency(0)}</p>
                        )}
                      </td>
                      <td className="p-4">
                        {getStatusBadge(effStatus(factuur))}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Email Preview Button with Hover (like Offertes) */}
                          <EmailPreviewButton
                            offerte={factuur}
                            templateType="factuur_reiniging"
                            onFetchPreview={() => fetchReinigingEmailPreview(factuur.id)}
                            previewData={reinigingEmailPreviewData[factuur.id]}
                            loading={reinigingEmailPreviewLoadingState[factuur.id]}
                            onQueue={() => handleQueueReinigingFactuurEmail(factuur)}
                            onSend={() => handleSendReinigingFactuurEmailDirect(factuur)}
                            sending={reinigingEmailSending[factuur.id]}
                            disabled={!customers.find(c => c.id === factuur.customer_id)?.email}
                          />
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`reiniging-factuur-menu-${factuur.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handlePreview(factuur)}>
                                <Eye className="w-4 h-4 mr-2" />
                                Bekijk PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDirectDownload(factuur)}>
                                <Download className="w-4 h-4 mr-2" />
                                Exporteer als PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handlePushFactuurToSharepoint(factuur)} data-testid={`push-factuur-sharepoint-${factuur.id}`}>
                                <FolderOpen className="w-4 h-4 mr-2" />
                                Push naar klantmap
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={async () => {
                                try {
                                  await factuurAPI.updateStatus(factuur.id, 'sent');
                                  toast.success('Status bijgewerkt naar Verzonden');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Fout bij updaten status');
                                }
                              }}>
                                <Send className="w-4 h-4 mr-2" />
                                Markeer als verzonden
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={async () => {
                                try {
                                  await factuurAPI.updateStatus(factuur.id, 'paid');
                                  toast.success('Status bijgewerkt naar Betaald');
                                  fetchData();
                                } catch (error) {
                                  toast.error('Fout bij updaten status');
                                }
                              }} className="text-green-600">
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Markeer als betaald
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openPaymentDialog(factuur, 'reiniging')} data-testid={`add-payment-${factuur.id}`}>
                                <HandCoins className="w-4 h-4 mr-2" />
                                Ontvangen betaling registreren
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openNoteDialog(factuur, 'reiniging')} data-testid={`add-note-${factuur.id}`}>
                                <StickyNote className="w-4 h-4 mr-2" />
                                {factuur.internal_note ? 'Notitie bewerken' : 'Notitie toevoegen'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openCreditDialog(factuur, 'reiniging')} data-testid={`credit-${factuur.id}`}>
                                <Euro className="w-4 h-4 mr-2" />
                                Crediteren
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleEdit(factuur)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Bewerken
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => setDeleteConfirm(factuur.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Verwijderen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Installatie Facturen Tab */}
        <TabsContent value="installatie" className="mt-4 space-y-6">
          {/* Meldingen: te versturen facturen per fase */}
          {(factuurReminders.voorschot.length > 0 || factuurReminders.na_montage.length > 0 || factuurReminders.na_keuring.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="factuur-reminders">
              {[
                { key: 'voorschot', title: 'Voorschotfactuur', sub: 'Offerte getekend', color: 'amber' },
                { key: 'na_montage', title: 'Factuur na montage/installatie', sub: 'Fase 1 + 2 aangevinkt', color: 'blue' },
                { key: 'na_keuring', title: 'Factuur na aansluiting/keuring', sub: 'Fase 2/3 aangevinkt', color: 'green' },
              ].map(tile => {
                const items = factuurReminders[tile.key] || [];
                const isExpanded = !!expandedReminderTiles[tile.key];
                const shownItems = isExpanded ? items : items.slice(0, 3);
                const colorMap = {
                  amber: 'bg-amber-500/5 border-amber-500/30 text-amber-600',
                  blue: 'bg-blue-500/5 border-blue-500/30 text-blue-600',
                  green: 'bg-green-500/5 border-green-500/30 text-green-600',
                };
                return (
                  <Card key={tile.key} className={items.length > 0 ? colorMap[tile.color].split(' text-')[0] : 'opacity-60'} data-testid={`reminder-tile-${tile.key}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <BellRing className={`w-4 h-4 ${items.length > 0 ? `text-${tile.color}-600` : 'text-muted-foreground'}`} />
                          {tile.title}
                        </h3>
                        <Badge variant={items.length > 0 ? 'default' : 'secondary'}>{items.length}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{tile.sub} — nog geen factuur verstuurd</p>
                      {items.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">Geen openstaande meldingen</p>
                      ) : (
                        <div className={`space-y-2 ${isExpanded ? '' : 'max-h-48 overflow-y-auto'}`}>
                          {shownItems.map(rem => (
                            <div
                              key={`${tile.key}-${rem.offerte_id}`}
                              className="border rounded-md p-2 bg-background text-sm cursor-pointer hover:border-primary hover:bg-muted/40 transition-colors group"
                              onClick={() => handleReminderClick(tile.key, rem)}
                              title="Klik om de factuur aan te maken en te bewerken"
                              data-testid={`reminder-${tile.key}-${rem.offerte_id}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium truncate">{rem.customer_name}</span>
                                <span className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className="font-semibold">{formatCurrency(rem.amount || 0)}</span>
                                  <button
                                    className="text-muted-foreground hover:text-red-500 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); handleDismissReminder(tile.key, rem); }}
                                    title="Melding verbergen (bv. al gefactureerd of afwijkende termijnen)"
                                    data-testid={`dismiss-reminder-${tile.key}-${rem.offerte_id}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                                <span className="truncate">{rem.offerte_number}{rem.address ? ` · ${rem.address}` : ''}</span>
                                <span className="flex-shrink-0">{rem.percentage}%</span>
                              </div>
                              {rem.created_by_name && (
                                <p className="text-xs text-muted-foreground truncate">
                                  <span className="text-muted-foreground/70">Aangemaakt door:</span> {rem.created_by_name}
                                </p>
                              )}
                              <p className="text-[11px] text-primary mt-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                <Edit className="w-3 h-3" /> Klik om factuur aan te maken & te bewerken
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      {items.length > 3 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full mt-2 h-7 text-xs"
                          onClick={() => setExpandedReminderTiles(prev => ({ ...prev, [tile.key]: !prev[tile.key] }))}
                          data-testid={`reminder-toggle-${tile.key}`}
                        >
                          {isExpanded ? 'Minder tonen' : `Meer bekijken (+${items.length - 3})`}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          
          {/* Accepted Installation Offertes Section */}
          {acceptedInstallationOffertes.length > 0 && (
            <Card className="bg-amber-500/5 border-amber-500/20">
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-amber-500" />
                  Getekende Installatie Offertes - Klaar voor facturatie ({acceptedInstallationOffertes.length})
                </h3>
                <div className="relative mb-3 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Zoek op naam of offertenummer..."
                    value={installOfferteSearch}
                    onChange={(e) => setInstallOfferteSearch(e.target.value)}
                    className="pl-10 h-9"
                    data-testid="install-offerte-search-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(() => {
                    const visible = acceptedInstallationOffertes.filter(o =>
                      (!installOfferteSearch ||
                      o.offerte_number?.toLowerCase().includes(installOfferteSearch.toLowerCase()) ||
                      o.customer_name?.toLowerCase().includes(installOfferteSearch.toLowerCase())) &&
                      (phaseFilter === 'all' || offertePhaseStage(o.id) === phaseFilter)
                    );
                    return (showAllInstallationOffertes || installOfferteSearch ? visible : visible.slice(0, 6));
                  })().map(offerte => {
                    // Get term percentages from offerte or use defaults
                    const terms = offerte.custom_payment_terms || [
                      { percentage: 40, description: 'Voorschot' },
                      { percentage: 50, description: 'Na montage' },
                      { percentage: 10, description: 'Eindfactuur' }
                    ];
                    
                    // Find which terms are paid / sent (created & sent but not paid) for this offerte
                    const offerteFacturen = installationFacturen.filter(f => 
                      f.offerte_id === offerte.id || f.reference === offerte.offerte_number
                    );
                    const paidTerms = offerteFacturen
                      .filter(f => f.status === 'paid' && f.term_number)
                      .map(f => f.term_number);
                    const sentTerms = offerteFacturen
                      .filter(f => (f.status === 'sent' || f.status === 'overdue') && f.term_number)
                      .map(f => f.term_number);
                    
                    return (
                    <div 
                      key={offerte.id}
                      className="p-3 bg-background rounded-lg border flex items-center justify-between"
                    >
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{offerte.offerte_number}</p>
                          <ProjectPhaseBadge offerte={offerte} phaseInfo={phaseMap[offerte.id]} />
                        </div>
                        <p className="text-sm text-muted-foreground">{offerte.customer_name}</p>
                        {(offerte.created_by_name || offerte.created_by) && (
                          <p className="text-xs text-muted-foreground truncate">
                            <span className="text-muted-foreground/70">Aangemaakt door:</span> {offerte.created_by_name || offerte.created_by}
                          </p>
                        )}
                        <p className="text-sm font-semibold">{formatCurrency(offerte.total_incl_vat)}</p>
                        <p className="text-xs">
                          Termijnen:{' '}
                          {terms.map((t, idx) => {
                            const termNr = idx + 1;
                            const isPaid = paidTerms.includes(termNr);
                            const isSent = !isPaid && sentTerms.includes(termNr);
                            const colorClass = isPaid
                              ? 'text-green-500 font-semibold'
                              : isSent
                              ? 'text-orange-500 font-semibold'
                              : 'text-muted-foreground';
                            return (
                              <span key={idx}>
                                {idx > 0 && ' / '}
                                <span className={`inline-flex items-center gap-0.5 ${colorClass}`} data-testid={`term-status-${offerte.id}-${termNr}`}>
                                  {t.percentage}%
                                  {isPaid && ' ✓'}
                                  {isSent && <Clock className="w-3 h-3 ml-0.5 inline" />}
                                </span>
                              </span>
                            );
                          })}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" data-testid={`create-invoice-dropdown-${offerte.id}`}>
                            <Euro className="w-4 h-4 mr-1" />
                            Factuur maken
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64">
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                            Per termijn factureren
                          </div>
                          {terms.map((t, idx) => (
                            t.percentage > 0 ? (
                              <DropdownMenuItem key={idx} onClick={() => openTermDialog(offerte)}>
                                <Clock className="w-4 h-4 mr-2" />
                                Termijn {idx + 1} - {t.description || `Termijn ${idx + 1}`} ({t.percentage}%)
                              </DropdownMenuItem>
                            ) : null
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );})}
                </div>
                {!installOfferteSearch && acceptedInstallationOffertes.length > 6 && (
                  <div className="mt-3 text-center">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAllInstallationOffertes(!showAllInstallationOffertes)}
                    >
                      {showAllInstallationOffertes 
                        ? 'Minder tonen' 
                        : `Meer bekijken (+${acceptedInstallationOffertes.length - 6})`}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Search and Filter for Installation */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Zoek op factuurnummer of klant..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="installatie-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="installatie-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="draft">Concept</SelectItem>
                <SelectItem value="sent">Verzonden</SelectItem>
                <SelectItem value="paid">Betaald</SelectItem>
                <SelectItem value="overdue">Te laat</SelectItem>
              </SelectContent>
            </Select>
            <Select value={phaseFilter} onValueChange={setPhaseFilter}>
              <SelectTrigger className="w-[180px]" data-testid="installatie-phase-filter">
                <SelectValue placeholder="Projectfase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle fases</SelectItem>
                <SelectItem value="te_plannen"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" />In te plannen</span></SelectItem>
                <SelectItem value="montage"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-500" />Montage</span></SelectItem>
                <SelectItem value="omvormer"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-yellow-400" />Omvormer</span></SelectItem>
                <SelectItem value="keuring"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-purple-500" />Keuring</span></SelectItem>
                <SelectItem value="afgewerkt"><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500" />Afgewerkt</span></SelectItem>
              </SelectContent>
            </Select>
            <Select value={allSortBy} onValueChange={setAllSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="installatie-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nummer">Sorteer: Factuurnummer</SelectItem>
                <SelectItem value="datum">Sorteer: Factuurdatum</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => {
                setManualFactuurForm({
                  factuur_number: '',
                  hide_unit_prices: false,
                  use_manual_subtotal: false,
                  manual_subtotal: 0,
                  customer_id: '',
                  property_id: '',
                  customer_name_override: '',
                  attn_override: '',
                  billing_address: '',
                  installation_address: '',
                  language: 'nl',
                  vat_percentage: 6,
                  due_days: 8,
                  notes: '',
                  reference: '',
                  items: [{ name: '', description: '', quantity: 1, unit_price: 0, unit: 'stuk', show_price: true, components: [] }],
                });
                setManualFactuurDialogOpen(true);
              }}
              data-testid="new-manual-install-factuur-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nieuwe Manuele Factuur
            </Button>
          </div>

          {/* Installation Facturen Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Totaal</p>
                <p className="text-2xl font-bold">{installationFacturen.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Concept</p>
                <p className="text-2xl font-bold" data-testid="kpi-inst-concept">{installationFacturen.filter(f => f.status === 'draft').length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Betaald</p>
                <p className="text-2xl font-bold text-green-500" data-testid="kpi-inst-betaald">{installationFacturen.filter(f => f.status === 'paid').length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Openstaand</p>
                <p className="text-2xl font-bold text-amber-500" data-testid="kpi-inst-openstaand">
                  {installationFacturen.filter(f => ['sent', 'overdue'].includes(effStatus(f))).length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Installation Facturen List */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="p-4 font-medium">Factuurnummer</th>
                      <th className="p-4 font-medium">Klant</th>
                      <th className="p-4 font-medium hidden md:table-cell">Datum</th>
                      <th className="p-4 font-medium hidden lg:table-cell">Vervaldatum</th>
                      <th className="p-4 font-medium text-right">Bedrag</th>
                      <th className="p-4 font-medium text-right">Openstaand</th>
                      <th className="p-4 font-medium">Betaling</th>
                      <th className="p-4 font-medium text-right">Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInstallationFacturen.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="p-12 text-center">
                          <Zap className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-lg font-medium text-muted-foreground">Geen installatie facturen gevonden</p>
                        </td>
                      </tr>
                    ) : (
                      filteredInstallationFacturen.map(factuur => (
                        <tr key={factuur.id} className="border-b hover:bg-muted/50" data-testid={`installation-factuur-row-${factuur.id}`}>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Zap className="w-4 h-4 text-amber-500" />
                              <span className="font-medium">{factuur.factuur_number}</span>
                              {factuur.internal_note && (
                                <button onClick={() => openNoteDialog(factuur, 'installatie')} title={factuur.internal_note} data-testid={`inst-note-indicator-${factuur.id}`}>
                                  <AlertCircle className="w-4 h-4 text-amber-500" />
                                </button>
                              )}
                            </div>
                            {factuur.offerte_number && (
                              <button
                                className="text-xs text-primary hover:underline block text-left"
                                onClick={() => openRefDialog(factuur)}
                                data-testid={`factuur-ref-btn-${factuur.id}`}
                                title="Open offerte-informatie"
                              >
                                Ref: {factuur.offerte_number}
                              </button>
                            )}
                          </td>
                          <td className="p-4">
                            <p>{factuur.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{factuur.property_address}</p>
                          </td>
                          <td className="p-4 hidden md:table-cell">{formatDate(factuur.factuur_date)}</td>
                          <td className="p-4 hidden lg:table-cell">{formatDate(factuur.due_date)}</td>
                          <td className="p-4 text-right font-semibold">{formatCurrency(factuur.total_incl_vat)}</td>
                          <td className="p-4 text-right">
                            {outstanding(factuur) > 0 ? (
                              <div>
                                <p className="font-semibold text-amber-500" data-testid={`inst-outstanding-${factuur.id}`}>{formatCurrency(outstanding(factuur))}</p>
                                {(factuur.amount_paid || 0) > 0 && <p className="text-xs text-muted-foreground">{formatCurrency(factuur.amount_paid)} ontvangen</p>}
                              </div>
                            ) : (
                              <p className="font-semibold text-green-500">{formatCurrency(0)}</p>
                            )}
                          </td>
                          <td className="p-4">
                            {/* Payment Progress - only show for term invoices */}
                            {factuur.term_number ? (
                              <div className="flex items-center gap-2">
                                <Badge variant={factuur.status === 'paid' ? 'default' : 'outline'} 
                                  className={factuur.status === 'paid' ? 'bg-green-500' : ''}>
                                  Termijn {factuur.term_number} ({factuur.term_percentage}%)
                                </Badge>
                                {factuur.status === 'paid' && <span className="text-green-600 text-xs">Betaald</span>}
                                {effStatus(factuur) === 'sent' && <span className="text-orange-500 text-xs" data-testid={`factuur-sent-label-${factuur.id}`}>Verstuurd</span>}
                                {effStatus(factuur) === 'overdue' && <span className="text-red-500 text-xs">Te laat</span>}
                              </div>
                            ) : (
                              getStatusBadge(effStatus(factuur))
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* Email Preview Button with Hover (like Offertes) */}
                              <EmailPreviewButton
                                offerte={factuur}
                                templateType={`installatie_factuur_${factuur.term_number === 1 ? 'voorschot' : factuur.term_number === 2 ? 'montage' : factuur.term_number === 3 ? 'eindfactuur' : 'algemeen'}`}
                                onFetchPreview={() => fetchEmailPreview(factuur.id)}
                                previewData={emailPreviewData[factuur.id]}
                                loading={emailPreviewLoadingState[factuur.id]}
                                onQueue={() => handleQueueFactuurEmail(factuur)}
                                onSend={() => handleSendFactuurEmailDirect(factuur)}
                                sending={emailSending[factuur.id]}
                                disabled={!customers.find(c => c.id === factuur.customer_id)?.email}
                              />
                              
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" data-testid={`installation-factuur-menu-${factuur.id}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleInstallationPreview(factuur)}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    Bekijk PDF
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleInstallationDirectDownload(factuur)}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Exporteer als PDF
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handlePushFactuurToSharepoint(factuur, true)} data-testid={`push-inst-factuur-sharepoint-${factuur.id}`}>
                                    <FolderOpen className="w-4 h-4 mr-2" />
                                    Push naar klantmap
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openInstallationFactuurEditor(factuur)}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Bewerken
                                  </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {/* Payment Terms Management */}
                                {factuur.payment_terms && factuur.payment_terms.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                      Betalingstermijnen
                                    </div>
                                    {factuur.payment_terms.map((term) => (
                                      <DropdownMenuItem 
                                        key={term.term_number}
                                        onClick={async () => {
                                          try {
                                            if (term.status === 'paid') {
                                              await installationFactuurAPI.unpayTerm(factuur.id, term.term_number);
                                              toast.success(`Termijn ${term.term_number} teruggezet`);
                                            } else {
                                              await installationFactuurAPI.payTerm(factuur.id, term.term_number);
                                              toast.success(`Termijn ${term.term_number} gemarkeerd als betaald`);
                                            }
                                            fetchData();
                                          } catch (error) {
                                            toast.error('Fout bij bijwerken termijn');
                                          }
                                        }}
                                        className={term.status === 'paid' ? 'text-green-600' : ''}
                                      >
                                        {term.status === 'paid' ? (
                                          <CheckCircle2 className="w-4 h-4 mr-2" />
                                        ) : (
                                          <Clock className="w-4 h-4 mr-2" />
                                        )}
                                        {term.percentage}% - {term.description?.substring(0, 20)}
                                        {term.status === 'paid' && ' ✓'}
                                      </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={async () => {
                                  await installationFactuurAPI.updateStatus(factuur.id, 'sent');
                                  toast.success('Status bijgewerkt naar Verzonden');
                                  fetchData();
                                }}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Markeer als verzonden
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={async () => {
                                  await installationFactuurAPI.updateStatus(factuur.id, 'paid');
                                  toast.success('Status bijgewerkt naar Betaald');
                                  fetchData();
                                }} className="text-green-600">
                                  <CheckCircle2 className="w-4 h-4 mr-2" />
                                  Markeer als betaald
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openPaymentDialog(factuur, 'installatie')} data-testid={`inst-add-payment-${factuur.id}`}>
                                  <HandCoins className="w-4 h-4 mr-2" />
                                  Ontvangen betaling registreren
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openNoteDialog(factuur, 'installatie')} data-testid={`inst-add-note-${factuur.id}`}>
                                  <StickyNote className="w-4 h-4 mr-2" />
                                  {factuur.internal_note ? 'Notitie bewerken' : 'Notitie toevoegen'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openCreditDialog(factuur, 'installatie')} data-testid={`inst-credit-${factuur.id}`}>
                                  <Euro className="w-4 h-4 mr-2" />
                                  Crediteren
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={() => setInstallationDeleteConfirm(factuur.id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Verwijderen
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Interventie Facturen Tab */}
        <TabsContent value="interventies" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="p-4">Factuur</th>
                      <th className="p-4">Klant</th>
                      <th className="p-4 hidden md:table-cell">Datum</th>
                      <th className="p-4 text-right">Bedrag</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {installationFacturen.filter(f => f.is_interventie).length === 0 ? (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Nog geen interventiefacturen — maak ze aan via de pagina Interventies</td></tr>
                    ) : (
                      installationFacturen.filter(f => f.is_interventie).map(factuur => (
                        <tr key={factuur.id} className="border-b hover:bg-muted/50" data-testid={`interventie-factuur-row-${factuur.id}`}>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Wrench className="w-4 h-4 text-orange-500" />
                              <span className="font-medium">{factuur.factuur_number}</span>
                            </div>
                            {factuur.reference && <p className="text-xs text-muted-foreground">{factuur.reference}</p>}
                          </td>
                          <td className="p-4">
                            <p>{factuur.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{factuur.property_address}</p>
                          </td>
                          <td className="p-4 hidden md:table-cell">{formatDate(factuur.factuur_date)}</td>
                          <td className="p-4 text-right font-semibold">{formatCurrency(factuur.total_incl_vat)}</td>
                          <td className="p-4">{getStatusBadge(effStatus(factuur))}</td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => openInstallationFactuurEditor(factuur)} data-testid={`interventie-factuur-edit-${factuur.id}`}>
                                <Edit className="w-3.5 h-3.5 mr-1.5" /> Bewerken
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" data-testid={`interventie-factuur-menu-${factuur.id}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleInstallationPreview(factuur)}>
                                    <Eye className="w-4 h-4 mr-2" /> Bekijk PDF
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleInstallationDirectDownload(factuur)}>
                                    <Download className="w-4 h-4 mr-2" /> Exporteer als PDF
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handlePushFactuurToSharepoint(factuur, true)}>
                                    <FolderOpen className="w-4 h-4 mr-2" /> Push naar klantmap
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={async () => {
                                    await installationFactuurAPI.updateStatus(factuur.id, 'sent');
                                    toast.success('Status bijgewerkt naar Verzonden');
                                    fetchData();
                                  }}>
                                    <Send className="w-4 h-4 mr-2" /> Markeer als verzonden
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={async () => {
                                    await installationFactuurAPI.updateStatus(factuur.id, 'paid');
                                    toast.success('Status bijgewerkt naar Betaald');
                                    fetchData();
                                  }} className="text-green-600">
                                    <CheckCircle2 className="w-4 h-4 mr-2" /> Markeer als betaald
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openPaymentDialog(factuur, 'installatie')}>
                                    <HandCoins className="w-4 h-4 mr-2" /> Ontvangen betaling registreren
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-destructive" onClick={() => setInstallationDeleteConfirm(factuur.id)}>
                                    <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alles Tab: alle facturen (installatie + reiniging) in 1 lijst */}
        <TabsContent value="alles" className="mt-4">
      <Card data-testid="all-invoices-card">
        <CardContent className="p-0">
          <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-lg">Alle facturen ({allInvoices.length})</h3>
              <p className="text-sm text-muted-foreground">Installatie- en reinigingsfacturen samen</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Sorteren op</Label>
              <Select value={allSortBy} onValueChange={setAllSortBy}>
                <SelectTrigger className="w-44 h-9" data-testid="all-invoices-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nummer">Factuurnummer</SelectItem>
                  <SelectItem value="datum">Factuurdatum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr className="text-left">
                  <th className="p-4 font-medium">Factuurnummer</th>
                  <th className="p-4 font-medium">Type</th>
                  <th className="p-4 font-medium">Klant</th>
                  <th className="p-4 font-medium">Datum</th>
                  <th className="p-4 font-medium">Vervaldatum</th>
                  <th className="p-4 font-medium text-right">Bedrag</th>
                  <th className="p-4 font-medium text-right">Openstaand</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {allInvoices.length === 0 ? (
                  <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Nog geen facturen</td></tr>
                ) : allInvoices.map(f => (
                  <tr key={`${f._type}-${f.id}`} className="border-b last:border-0 hover:bg-muted/30" data-testid={`all-invoice-row-${f.id}`}>
                    <td className="p-4 font-medium">
                      {f.factuur_number || '-'}
                      {f.internal_note && (
                        <button onClick={() => openNoteDialog(f, f._type)} title={f.internal_note} data-testid={`all-note-indicator-${f.id}`}>
                          <AlertCircle className="w-4 h-4 inline ml-1.5 text-amber-500" />
                        </button>
                      )}
                    </td>
                    <td className="p-4">
                      {f._type === 'installatie' ? (
                        <Badge variant="outline" className="gap-1 text-amber-500 border-amber-500/40"><Zap className="w-3 h-3" /> Installatie</Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 text-sky-500 border-sky-500/40"><Sparkles className="w-3 h-3" /> Reiniging</Badge>
                      )}
                    </td>
                    <td className="p-4">{f.customer_name || '-'}</td>
                    <td className="p-4 text-sm">{f.factuur_date || '-'}</td>
                    <td className="p-4 text-sm">{f.due_date || '-'}</td>
                    <td className="p-4 text-right font-medium">{formatCurrency(f.total_incl_vat || 0)}</td>
                    <td className="p-4 text-right">
                      {outstanding(f) > 0 ? (
                        <div>
                          <p className="font-semibold text-amber-500">{formatCurrency(outstanding(f))}</p>
                          {(f.amount_paid || 0) > 0 && <p className="text-xs text-muted-foreground">{formatCurrency(f.amount_paid)} ontvangen</p>}
                        </div>
                      ) : (
                        <span className="font-semibold text-green-500">{formatCurrency(0)}</span>
                      )}
                    </td>
                    <td className="p-4">{getStatusBadge(effStatus(f))}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Download PDF"
                          onClick={() => f._type === 'installatie' ? handleInstallationDirectDownload(f) : handleDirectDownload(f)}
                          data-testid={`all-invoice-pdf-${f.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`all-invoice-menu-${f.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openPaymentDialog(f, f._type)} data-testid={`all-add-payment-${f.id}`}>
                              <HandCoins className="w-4 h-4 mr-2" />
                              Ontvangen betaling registreren
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openNoteDialog(f, f._type)} data-testid={`all-add-note-${f.id}`}>
                              <StickyNote className="w-4 h-4 mr-2" />
                              {f.internal_note ? 'Notitie bewerken' : 'Notitie toevoegen'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openCreditDialog(f, f._type)} data-testid={`all-credit-${f.id}`}>
                              <Euro className="w-4 h-4 mr-2" />
                              Crediteren
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Creditnota's Tab */}
        <TabsContent value="creditnotas" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full">
                <thead className="border-b"><tr className="text-left">
                  <th className="p-4 font-medium">Nummer</th>
                  <th className="p-4 font-medium">Klant</th>
                  <th className="p-4 font-medium">Betreft factuur</th>
                  <th className="p-4 font-medium">Datum</th>
                  <th className="p-4 font-medium">Omschrijving</th>
                  <th className="p-4 font-medium text-right">Bedrag incl. BTW</th>
                  <th className="p-4 font-medium text-right">Acties</th>
                </tr></thead>
                <tbody>
                  {creditnotas.length === 0 ? (
                    <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">
                      Nog geen creditnota's — gebruik "Crediteren" in het ⋯-menu van een factuur
                    </td></tr>
                  ) : creditnotas.map(cn => (
                    <tr key={cn.id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`creditnota-row-${cn.id}`}>
                      <td className="p-4 font-mono font-medium">{cn.creditnota_number}</td>
                      <td className="p-4">{cn.customer_name}</td>
                      <td className="p-4">{cn.source_factuur_number}</td>
                      <td className="p-4 text-sm">{cn.date}</td>
                      <td className="p-4 text-sm max-w-[240px] truncate" title={cn.description}>{cn.description}</td>
                      <td className="p-4 text-right font-semibold text-red-500">-{formatCurrency(cn.amount_incl)}</td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" title="Nummer wijzigen" onClick={() => setEditCnDialog({ id: cn.id, creditnota_number: cn.creditnota_number })} data-testid={`creditnota-edit-${cn.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Download PDF" onClick={() => downloadCreditnotaPdf(cn)} data-testid={`creditnota-pdf-${cn.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" title="Verwijderen" onClick={() => deleteCreditnota(cn)} data-testid={`creditnota-delete-${cn.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Offerte-referentie: PDF preview */}
      <Dialog open={!!refDialog} onOpenChange={(o) => !o && closeRefDialog()}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b flex-shrink-0">
            <div className="flex items-center justify-between gap-3 pr-8">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Offerte {refDialog?.offerte?.offerte_number || refDialog?.number || ''}
                </DialogTitle>
                <DialogDescription>{refDialog?.offerte?.customer_name || ''}</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-muted/50" data-testid="ref-offerte-dialog">
            {refDialog?.loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-muted-foreground">PDF laden...</p>
                </div>
              </div>
            ) : refDialog?.pdfUrl ? (
              <PdfViewer pdfUrl={refDialog.pdfUrl} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Kon PDF niet laden</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Creditnotanummer wijzigen dialog */}
      <Dialog open={!!editCnDialog} onOpenChange={(o) => !o && setEditCnDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Creditnotanummer wijzigen</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <Label>Nummer</Label>
            <Input value={editCnDialog?.creditnota_number || ''}
              onChange={(e) => setEditCnDialog(p => ({ ...p, creditnota_number: e.target.value }))}
              data-testid="edit-cn-number-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCnDialog(null)}>Annuleren</Button>
            <Button data-testid="edit-cn-save-btn" onClick={async () => {
              try {
                await creditnotaAPI.update(editCnDialog.id, { creditnota_number: editCnDialog.creditnota_number?.trim() });
                toast.success('Creditnotanummer bijgewerkt');
                setEditCnDialog(null);
                fetchData();
              } catch (e) { toast.error(e.response?.data?.detail || 'Bijwerken mislukt'); }
            }}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crediteren dialog */}
      <Dialog open={!!creditDialog} onOpenChange={(o) => !o && setCreditDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><Euro className="w-5 h-5 inline mr-2" />Crediteren — {creditDialog?.factuur?.factuur_number}</DialogTitle>
            <DialogDescription>{creditDialog?.factuur?.customer_name}. De creditnota toont negatieve bedragen, de wettelijke btw-vermelding en géén betaalinstructies.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label>Creditnotanummer</Label>
            <Input value={creditForm.creditnota_number}
              onChange={(e) => setCreditForm(p => ({ ...p, creditnota_number: e.target.value }))}
              placeholder="Automatisch (CN-reeks)"
              data-testid="credit-number-input" />
            <p className="text-xs text-muted-foreground">Leeg = automatisch volgend nummer in de CN-reeks</p>
          </div>
          <div className="space-y-1">
            <Label>Te crediteren bedrag excl. BTW (€) *</Label>
            <Input type="text" inputMode="decimal" value={creditForm.amount_excl}
              onChange={(e) => setCreditForm(p => ({ ...p, amount_excl: e.target.value }))}
              data-testid="credit-amount-input" />
            {creditDialog && (
              <p className="text-xs text-muted-foreground">
                Factuurbedrag: {formatCurrency(creditDialog.factuur.total_excl_vat ?? creditDialog.factuur.subtotal ?? 0)} excl. — incl. {creditDialog.factuur.vat_percentage ?? 21}% BTW wordt dit {formatCurrency((parseFloat(String(creditForm.amount_excl).replace(',', '.')) || 0) * (1 + (creditDialog.factuur.vat_percentage ?? 21) / 100))}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Omschrijving</Label>
            <Textarea rows={2} value={creditForm.description}
              onChange={(e) => setCreditForm(p => ({ ...p, description: e.target.value }))}
              data-testid="credit-description-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDialog(null)}>Annuleren</Button>
            <Button onClick={saveCreditnota} data-testid="credit-save-btn"><Euro className="w-4 h-4 mr-2" />Creditnota aanmaken</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Betaling registreren dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(o) => !o && setPaymentDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><HandCoins className="w-5 h-5 inline mr-2" />Ontvangen betaling — {paymentDialog?.factuur?.factuur_number}</DialogTitle>
            <DialogDescription>{paymentDialog?.factuur?.customer_name}</DialogDescription>
          </DialogHeader>
          {paymentDialog && (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="border rounded-lg p-2">
                  <p className="text-xs text-muted-foreground">Factuurbedrag</p>
                  <p className="font-semibold">{formatCurrency(paymentDialog.factuur.total_incl_vat || 0)}</p>
                </div>
                <div className="border rounded-lg p-2">
                  <p className="text-xs text-muted-foreground">Reeds ontvangen</p>
                  <p className="font-semibold text-green-500">{formatCurrency(paymentDialog.factuur.amount_paid || 0)}</p>
                </div>
                <div className="border rounded-lg p-2">
                  <p className="text-xs text-muted-foreground">Openstaand</p>
                  <p className="font-semibold text-amber-500" data-testid="payment-dialog-outstanding">{formatCurrency(outstanding(paymentDialog.factuur))}</p>
                </div>
              </div>
              {(paymentDialog.factuur.amount_paid || 0) > (paymentDialog.factuur.total_incl_vat || 0) + 0.009 && (
                <div className="rounded-lg border border-red-300 bg-red-500/10 text-red-600 text-sm px-3 py-2 font-medium" data-testid="overpaid-warning">
                  ⚠ Te veel betaald: {formatCurrency((paymentDialog.factuur.amount_paid || 0) - (paymentDialog.factuur.total_incl_vat || 0))} méér dan het factuurbedrag
                </div>
              )}
              {(paymentDialog.factuur.payments || []).length > 0 && (
                <div className="border rounded-lg divide-y max-h-36 overflow-y-auto">
                  {paymentDialog.factuur.payments.map(p => (
                    <div key={p.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <span>{p.date} — <strong>{formatCurrency(p.amount)}</strong>{p.note ? ` (${p.note})` : ''}</span>
                      <button className="text-destructive hover:opacity-70" onClick={() => removePayment(p.id)} title="Betaling verwijderen" data-testid={`delete-payment-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Bedrag (€) *</Label>
                  <Input type="number" step="0.01" min="0" value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                    placeholder={String(outstanding(paymentDialog.factuur).toFixed(2))}
                    data-testid="payment-amount-input" />
                </div>
                <div className="space-y-1">
                  <Label>Datum</Label>
                  <Input type="date" value={paymentForm.date}
                    onChange={(e) => setPaymentForm(p => ({ ...p, date: e.target.value }))}
                    data-testid="payment-date-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Opmerking (optioneel)</Label>
                <Input value={paymentForm.note}
                  onChange={(e) => setPaymentForm(p => ({ ...p, note: e.target.value }))}
                  placeholder="Bijv. overschrijving, deelbetaling 1..."
                  data-testid="payment-note-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPaymentDialog(null)}>Annuleren</Button>
                <Button onClick={savePayment} data-testid="payment-save-btn">
                  <HandCoins className="w-4 h-4 mr-2" />Registreren
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Interne notitie dialog */}
      <Dialog open={!!noteDialog} onOpenChange={(o) => !o && setNoteDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><StickyNote className="w-5 h-5 inline mr-2" />Notitie — {noteDialog?.factuur?.factuur_number}</DialogTitle>
            <DialogDescription>Interne info, bv. waarom er niet of gedeeltelijk betaald wordt. Niet zichtbaar voor de klant.</DialogDescription>
          </DialogHeader>
          <Textarea rows={6} value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="Bijv. klant gebeld 20/08 — betaalt eind deze maand het resterende saldo…"
            data-testid="invoice-note-textarea" />
          {noteDialog?.factuur?.internal_note_updated_by && (
            <p className="text-xs text-muted-foreground">Laatst bijgewerkt door {noteDialog.factuur.internal_note_updated_by}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(null)}>Annuleren</Button>
            <Button onClick={saveNote} data-testid="invoice-note-save-btn">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFactuur ? 'Factuur Bewerken' : 'Nieuwe Factuur'}
            </DialogTitle>
            <DialogDescription>
              {selectedOfferte && `Gebaseerd op offerte ${selectedOfferte.offerte_number}`}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Factuurnummer</Label>
                <Input
                  value={formData.factuur_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, factuur_number: e.target.value }))}
                  placeholder="Automatisch gegenereerd"
                />
                <p className="text-xs text-muted-foreground">Laat leeg voor automatische nummering</p>
              </div>
              <div className="space-y-2">
                <Label>Klant</Label>
                <Select 
                  value={formData.customer_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, customer_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer klant" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Factuurdatum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.factuur_date ? format(parse(formData.factuur_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.factuur_date ? parse(formData.factuur_date, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => date && setFormData(prev => ({ ...prev, factuur_date: format(date, 'yyyy-MM-dd') }))}
                      locale={nl}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Vervaldatum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.due_date ? format(parse(formData.due_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.due_date ? parse(formData.due_date, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => date && setFormData(prev => ({ ...prev, due_date: format(date, 'yyyy-MM-dd') }))}
                      locale={nl}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Referentie</Label>
              <Input
                value={formData.reference}
                onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="bv. 2026-3098 26032026"
              />
            </div>

            {/* Language Selection */}
            <div className="space-y-2">
              <Label>Taal PDF</Label>
              <Select 
                value={formData.language || 'nl'} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, language: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer taal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nl">Nederlands</SelectItem>
                  <SelectItem value="fr">Frans</SelectItem>
                  <SelectItem value="en">Engels</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">De PDF wordt automatisch vertaald naar de geselecteerde taal</p>
            </div>

            {/* Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Factuurregels</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Regel toevoegen
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input
                      className="flex-1"
                      placeholder="Beschrijving"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                    />
                    <Input
                      className="w-20"
                      type="text"
                      inputMode="decimal"
                      placeholder="Aantal"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                    />
                    <Input
                      className="w-28"
                      type="text"
                      inputMode="decimal"
                      placeholder="Prijs"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                    />
                    <div className="w-24 text-right pt-2 font-medium">
                      {formatCurrency(parseDec(item.quantity) * parseDec(item.unit_price))}
                    </div>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeItem(index)}
                      disabled={formData.items.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between">
                <span>Subtotaal excl. BTW:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Korting (€):</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  className="w-28 text-right"
                  value={formData.discount}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount: e.target.value }))}
                  data-testid="factuur-discount-input"
                />
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between">
                  <span>Totaal excl. BTW:</span>
                  <span className="font-medium">{formatCurrency(totalExcl)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span>BTW</span>
                  <Select 
                    value={formData.vat_percentage.toString()} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, vat_percentage: parseFloat(v) }))}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="6">6%</SelectItem>
                      <SelectItem value="21">21%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <span className="font-medium">{formatCurrency(vatAmount)}</span>
              </div>
              {formData.vat_percentage === 0 && (
                <p className="text-xs text-muted-foreground italic" data-testid="btw-verlegd-note">
                  BTW verlegd — de mededeling "Verlegging van heffing" wordt automatisch op de factuur vermeld.
                </p>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Totaal incl. BTW:</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Opmerkingen</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Extra opmerkingen..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Opslaan...' : (editingFactuur ? 'Bijwerken' : 'Factuur Aanmaken')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Factuur verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm)} className="bg-destructive text-destructive-foreground">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Installation Factuur Delete Confirmation */}
      <AlertDialog open={!!installationDeleteConfirm} onOpenChange={() => setInstallationDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Installatie factuur verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleInstallationDelete(installationDeleteConfirm)} className="bg-destructive text-destructive-foreground">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={closePreview}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {previewFactuur?.factuur_number}
                </DialogTitle>
                <DialogDescription>
                  {previewFactuur?.customer_name}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden bg-muted/50">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-muted-foreground">PDF laden...</p>
                </div>
              </div>
            ) : previewUrl ? (
              <PdfViewer pdfUrl={previewUrl} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Kon PDF niet laden</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Installation Factuur PDF Preview Modal */}
      <Dialog open={installationPreviewOpen} onOpenChange={closeInstallationPreview}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  {installationPreviewFactuur?.factuur_number}
                </DialogTitle>
                <DialogDescription>
                  {installationPreviewFactuur?.customer_name}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden bg-muted/50">
            {installationPreviewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-muted-foreground">PDF laden...</p>
                </div>
              </div>
            ) : installationPreviewUrl ? (
              <PdfViewer pdfUrl={installationPreviewUrl} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Kon PDF niet laden</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Term Invoice Creation Dialog */}
      <Dialog open={termDialogOpen} onOpenChange={setTermDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Termijn Factuur Maken
            </DialogTitle>
            <DialogDescription>
              {selectedOfferteForTerm?.offerte_number} - {selectedOfferteForTerm?.customer_name}
              <br />
              <span className="font-semibold">Totaal: {formatCurrency(selectedOfferteForTerm?.total_incl_vat || 0)}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-sm text-muted-foreground mb-3">
              Kies een termijn en pas het percentage aan indien nodig:
            </div>
            
            {termPercentages.map((term, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
              >
                <div className="flex-1">
                  <p className="font-medium">Termijn {idx + 1} - {term.description || `Termijn ${idx + 1}`}</p>
                  <p className="text-sm text-muted-foreground">
                    {term.percentage > 0
                      ? formatCurrency((selectedOfferteForTerm?.total_incl_vat || 0) * (term.percentage / 100))
                      : 'Wordt overgeslagen (0%)'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={term.percentage}
                    onChange={(e) => {
                      const newTerms = [...termPercentages];
                      newTerms[idx] = { ...newTerms[idx], percentage: e.target.value };
                      setTermPercentages(newTerms);
                    }}
                    className="w-16 text-center"
                  />
                  <span className="text-sm">%</span>
                  <Button 
                    size="sm"
                    onClick={() => handleCreateTermInvoice(idx + 1, parseDec(term.percentage))}
                    disabled={creatingTerm || parseDec(term.percentage) <= 0}
                    data-testid={`create-term-${idx + 1}-btn`}
                  >
                    {creatingTerm ? '...' : 'Maak'}
                  </Button>
                  {termPercentages.length > 2 && idx === termPercentages.length - 1 && (
                    <button
                      className="text-muted-foreground hover:text-red-500"
                      onClick={() => setTermPercentages(termPercentages.slice(0, -1))}
                      title="Laatste termijn verwijderen"
                      data-testid="remove-term-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            <div className="flex items-center justify-between gap-2">
              {termPercentages.length < 4 ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTermPercentages([...termPercentages, { percentage: 0, description: `Termijn ${termPercentages.length + 1}` }])}
                  data-testid="add-term-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Termijn toevoegen
                </Button>
              ) : <span />}
              <Button
                variant="secondary"
                size="sm"
                onClick={saveTermsToOfferte}
                disabled={savingTerms}
                data-testid="save-terms-btn"
              >
                {savingTerms ? 'Opslaan...' : 'Termijnen opslaan op offerte'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Tip: zet een termijn op 0% om die over te slaan (bv. 0% / 90% / 10%). Opslaan zorgt dat de tegel en meldingen deze verdeling volgen.
            </p>
            
            {(() => {
              const total = termPercentages.reduce((sum, t) => sum + (t.percentage || 0), 0);
              return (
                <p className={`text-sm ${total === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                  Totaal: {total}% {total !== 100 && '(standaard is 100%)'}
                </p>
              );
            })()}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermDialogOpen(false)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Installation Factuur Edit Dialog - Full detail editing like Offerte */}
      <Dialog open={!!editingInstallationFactuur} onOpenChange={() => setEditingInstallationFactuur(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Factuur bewerken - {editingInstallationFactuur?.factuur_number}</DialogTitle>
            <DialogDescription>
              Pas alle factuurdetails aan: items, prijzen, specificaties
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            {/* Row 1: Factuur Number and Customer Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Factuurnummer</Label>
                <Input
                  value={editingInstallationFactuur?.factuur_number || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({
                    ...prev,
                    factuur_number: e.target.value
                  }))}
                  placeholder="bv. INSTF-2026-0001"
                />
              </div>
              <div className="space-y-2">
                <Label>Klant</Label>
                <Input
                  value={editingInstallationFactuur?.customer_name || ''}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>

            {/* Row 1b: Klantnaam + T.a.v. overrides */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Klantnaam op factuur <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Input
                  value={editingInstallationFactuur?.customer_name_override || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({ ...prev, customer_name_override: e.target.value }))}
                  placeholder="Leeg = naam van de klant"
                  data-testid="edit-factuur-name-override"
                />
              </div>
              <div className="space-y-2">
                <Label>T.a.v. <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Input
                  value={editingInstallationFactuur?.attn_override || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({ ...prev, attn_override: e.target.value }))}
                  placeholder="bv. Jan Peeters"
                  data-testid="edit-factuur-attn-override"
                />
              </div>
            </div>

            {/* Row 1b: Facturatie- en projectadres (altijd zichtbaar/aanpasbaar) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Facturatieadres <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Input
                  value={editingInstallationFactuur?.billing_address || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({ ...prev, billing_address: e.target.value }))}
                  placeholder="bv. Nieuwstraat 12, 1000 Brussel"
                  data-testid="edit-factuur-billing-address"
                />
                <p className="text-xs text-muted-foreground">Leeg = facturatieadres van de klant. Gebruik een komma vóór postcode + gemeente</p>
              </div>
              <div className="space-y-2">
                <Label>Projectadres <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Input
                  value={editingInstallationFactuur?.installation_address || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({ ...prev, installation_address: e.target.value }))}
                  placeholder="bv. Werfstraat 8, 9000 Gent"
                  data-testid="edit-factuur-installation-address"
                />
                <p className="text-xs text-muted-foreground">Wordt als "Installatie-adres" op de PDF getoond</p>
              </div>
            </div>
            
            {/* Row 2: Dates and VAT */}
            <div className="grid grid-cols-3 gap-4 p-4 border rounded-lg bg-muted/30">
              <div className="space-y-2">
                <Label>Factuurdatum</Label>
                <Input
                  type="date"
                  value={editingInstallationFactuur?.factuur_date || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({
                    ...prev,
                    factuur_date: e.target.value
                  }))}
                  data-testid="edit-installation-factuur-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Vervaldatum</Label>
                <Input
                  type="date"
                  value={editingInstallationFactuur?.due_date || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({
                    ...prev,
                    due_date: e.target.value
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>BTW %</Label>
                <Select
                  value={String(editingInstallationFactuur?.vat_percentage ?? 6)}
                  onValueChange={(v) => setEditingInstallationFactuur(prev => ({
                    ...prev,
                    vat_percentage: parseFloat(v)
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% BTW</SelectItem>
                    <SelectItem value="6">6% BTW</SelectItem>
                    <SelectItem value="21">21% BTW</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Datum plaatsing PV</Label>
                <Input
                  type="date"
                  data-testid="factuur-installation-date"
                  value={editingInstallationFactuur?.installation_date || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({
                    ...prev,
                    installation_date: e.target.value
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Datum aansluiting</Label>
                <Input
                  type="date"
                  data-testid="factuur-connection-date"
                  value={editingInstallationFactuur?.connection_date || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({
                    ...prev,
                    connection_date: e.target.value
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Datum keuring</Label>
                <Input
                  type="date"
                  data-testid="factuur-inspection-date"
                  value={editingInstallationFactuur?.inspection_date || ''}
                  onChange={(e) => setEditingInstallationFactuur(prev => ({
                    ...prev,
                    inspection_date: e.target.value
                  }))}
                />
              </div>
            </div>

            {/* Items Section - Full editing like Offerte */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Items</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newItem = {
                      name: 'Nieuw item',
                      description: '',
                      quantity: 1,
                      unit_price: 0,
                      unit: 'stuk',
                      show_offerte_products: true,
                      components: []
                    };
                    setInstallationEditItems([...installationEditItems, newItem]);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Item toevoegen
                </Button>
              </div>
              
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted rounded-t-lg text-sm font-medium">
                <div className="col-span-5">Omschrijving</div>
                <div className="col-span-2 text-center">Aantal</div>
                <div className="col-span-2 text-center">Eenheidsprijs</div>
                <div className="col-span-2 text-right">Totaal</div>
                <div className="col-span-1 text-center">Acties</div>
              </div>
              
              {/* Items */}
              {installationEditItems.map((item, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-3">
                  {/* Main item row */}
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-5">
                      <Input
                        value={item.name || ''}
                        onChange={(e) => {
                          const newItems = [...installationEditItems];
                          newItems[index] = { ...newItems[index], name: e.target.value };
                          setInstallationEditItems(newItems);
                        }}
                        placeholder="Naam item"
                      />
                      <Input
                        className="mt-2 text-sm"
                        value={item.description || ''}
                        onChange={(e) => {
                          const newItems = [...installationEditItems];
                          newItems[index] = { ...newItems[index], description: e.target.value };
                          setInstallationEditItems(newItems);
                        }}
                        placeholder="Beschrijving (optioneel)"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.quantity ?? 1}
                        onChange={(e) => {
                          const newItems = [...installationEditItems];
                          newItems[index] = { ...newItems[index], quantity: e.target.value };
                          setInstallationEditItems(newItems);
                        }}
                        className="text-center"
                      />
                      <p className="text-xs text-muted-foreground text-center mt-1">{item.unit || 'stuk'}</p>
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.unit_price ?? 0}
                        onChange={(e) => {
                          const newItems = [...installationEditItems];
                          newItems[index] = { ...newItems[index], unit_price: e.target.value };
                          setInstallationEditItems(newItems);
                        }}
                        className="text-center"
                      />
                    </div>
                    <div className="col-span-2 text-right font-medium pt-2">
                      €{(parseDec(item.quantity ?? 1) * parseDec(item.unit_price)).toFixed(2)}
                    </div>
                    <div className="col-span-1 flex items-center justify-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={item.show_offerte_products !== false ? 'text-primary' : 'text-muted-foreground'}
                        onClick={() => {
                          const newItems = [...installationEditItems];
                          newItems[index] = { ...newItems[index], show_offerte_products: !(newItems[index].show_offerte_products !== false) };
                          setInstallationEditItems(newItems);
                        }}
                        title={item.show_offerte_products !== false ? 'Details op PDF' : 'Verborgen op PDF'}
                      >
                        {item.show_offerte_products !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          const newItems = installationEditItems.filter((_, i) => i !== index);
                          setInstallationEditItems(newItems);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Specifications/Components */}
                  <div className="pl-4 border-l-2 border-muted space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Specificaties:</p>
                    {(item.components || []).map((comp, compIndex) => (
                      <div key={compIndex} className="flex items-center gap-2">
                        <span className="text-muted-foreground">-</span>
                        <Input
                          className="flex-1 text-sm h-8"
                          value={comp.description || ''}
                          onChange={(e) => {
                            const newItems = [...installationEditItems];
                            const newComps = [...(newItems[index].components || [])];
                            newComps[compIndex] = { ...newComps[compIndex], description: e.target.value };
                            newItems[index] = { ...newItems[index], components: newComps };
                            setInstallationEditItems(newItems);
                          }}
                          placeholder="Specificatie"
                        />
                        <Input
                          type="text"
                          inputMode="decimal"
                          className="w-16 text-sm h-8 text-center"
                          value={comp.quantity ?? 0}
                          onChange={(e) => {
                            const newItems = [...installationEditItems];
                            const newComps = [...(newItems[index].components || [])];
                            newComps[compIndex] = { ...newComps[compIndex], quantity: e.target.value };
                            newItems[index] = { ...newItems[index], components: newComps };
                            setInstallationEditItems(newItems);
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => {
                            const newItems = [...installationEditItems];
                            const newComps = (newItems[index].components || []).filter((_, i) => i !== compIndex);
                            newItems[index] = { ...newItems[index], components: newComps };
                            setInstallationEditItems(newItems);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => {
                        const newItems = [...installationEditItems];
                        const newComps = [...(newItems[index].components || []), { description: '', quantity: 0 }];
                        newItems[index] = { ...newItems[index], components: newComps };
                        setInstallationEditItems(newItems);
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Specificatie toevoegen
                    </Button>
                  </div>
                </div>
              ))}
              
              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                {editingInstallationFactuur?.use_manual_subtotal ? (
                  <>
                    <div className="flex justify-between items-center text-sm">
                      <span>Subtotaal excl. BTW <span className="text-xs text-muted-foreground">(manueel)</span></span>
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">€</span>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={editingInstallationFactuur?.manual_subtotal ?? ''}
                          onChange={(e) => setEditingInstallationFactuur(prev => ({ ...prev, manual_subtotal: e.target.value }))}
                          className="w-32 h-8 text-right"
                          data-testid="edit-factuur-manual-subtotal"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>BTW {editingInstallationFactuur?.vat_percentage ?? 6}%</span>
                      <span className="font-medium">
                        €{(parseDec(editingInstallationFactuur?.manual_subtotal) * ((editingInstallationFactuur?.vat_percentage ?? 6) / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>Totaal incl. BTW</span>
                      <span>
                        €{(parseDec(editingInstallationFactuur?.manual_subtotal) * (1 + (editingInstallationFactuur?.vat_percentage ?? 6) / 100)).toFixed(2)}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span>Subtotaal excl. BTW</span>
                      <span className="font-medium">
                        €{installationEditItems.reduce((sum, item) => sum + parseDec(item.quantity ?? 1) * parseDec(item.unit_price), 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>BTW {editingInstallationFactuur?.vat_percentage ?? 6}%</span>
                      <span className="font-medium">
                        €{(installationEditItems.reduce((sum, item) => sum + parseDec(item.quantity ?? 1) * parseDec(item.unit_price), 0) * ((editingInstallationFactuur?.vat_percentage ?? 6) / 100)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>Totaal incl. BTW</span>
                      <span>
                        €{(installationEditItems.reduce((sum, item) => sum + parseDec(item.quantity ?? 1) * parseDec(item.unit_price), 0) * (1 + (editingInstallationFactuur?.vat_percentage ?? 6) / 100)).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Linked offerte products & specifications - fully editable like the Offerte editor */}
            {(loadingLinkedOfferte || linkedOfferteId) && (
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Producten & specificaties (uit offerte {editingInstallationFactuur?.offerte_number || ''})</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Aanpassingen worden opgeslagen op de gekoppelde offerte en zijn zichtbaar op de PDF.
                    </p>
                  </div>
                  {linkedOfferteId && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setLinkedOfferteItems([
                          ...linkedOfferteItems,
                          { name: 'Nieuw item', description: '', quantity: 1, unit_price: 0, unit: 'stuk', is_optional: false, show_price: true, components: [], sub_products: [] },
                        ]);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Item toevoegen
                    </Button>
                  )}
                </div>

                {loadingLinkedOfferte && (
                  <div className="text-sm text-muted-foreground py-4 text-center">Offerte laden...</div>
                )}

                {!loadingLinkedOfferte && linkedOfferteItems.length === 0 && (
                  <div className="text-sm text-muted-foreground py-4 text-center border rounded-lg">
                    Geen items in de gekoppelde offerte.
                  </div>
                )}

                {!loadingLinkedOfferte && linkedOfferteItems.map((item, index) => (
                  <div key={`off-${index}`} className="border rounded-lg p-3 space-y-3 bg-muted/20">
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-5">
                        <Input
                          value={item.name || ''}
                          onChange={(e) => {
                            const arr = [...linkedOfferteItems];
                            arr[index] = { ...arr[index], name: e.target.value };
                            setLinkedOfferteItems(arr);
                          }}
                          placeholder="Naam item"
                        />
                        <Input
                          className="mt-2 text-sm"
                          value={item.description || ''}
                          onChange={(e) => {
                            const arr = [...linkedOfferteItems];
                            arr[index] = { ...arr[index], description: e.target.value };
                            setLinkedOfferteItems(arr);
                          }}
                          placeholder="Beschrijving (optioneel)"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={item.quantity ?? 1}
                          onChange={(e) => {
                            const arr = [...linkedOfferteItems];
                            arr[index] = { ...arr[index], quantity: e.target.value };
                            setLinkedOfferteItems(arr);
                          }}
                          className="text-center"
                        />
                        <p className="text-xs text-muted-foreground text-center mt-1">{item.unit || 'stuk'}</p>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={item.unit_price ?? 0}
                          onChange={(e) => {
                            const arr = [...linkedOfferteItems];
                            arr[index] = { ...arr[index], unit_price: e.target.value };
                            setLinkedOfferteItems(arr);
                          }}
                          className="text-center"
                        />
                      </div>
                      <div className="col-span-2 text-right font-medium pt-2">
                        €{(parseDec(item.quantity ?? 1) * parseDec(item.unit_price)).toFixed(2)}
                      </div>
                      <div className="col-span-1 flex items-center justify-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setLinkedOfferteItems(linkedOfferteItems.filter((_, i) => i !== index));
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Components / specifications */}
                    <div className="pl-4 border-l-2 border-muted space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Specificaties:</p>
                      {(item.components || []).map((comp, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <span className="text-muted-foreground">-</span>
                          <Input
                            className="flex-1 text-sm h-8"
                            value={comp.description || ''}
                            onChange={(e) => {
                              const arr = [...linkedOfferteItems];
                              const comps = [...(arr[index].components || [])];
                              comps[ci] = { ...comps[ci], description: e.target.value };
                              arr[index] = { ...arr[index], components: comps };
                              setLinkedOfferteItems(arr);
                            }}
                            placeholder="Specificatie"
                          />
                          <Input
                            type="text"
                            inputMode="decimal"
                            className="w-16 text-sm h-8 text-center"
                            value={comp.quantity ?? 0}
                            onChange={(e) => {
                              const arr = [...linkedOfferteItems];
                              const comps = [...(arr[index].components || [])];
                              comps[ci] = { ...comps[ci], quantity: e.target.value };
                              arr[index] = { ...arr[index], components: comps };
                              setLinkedOfferteItems(arr);
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => {
                              const arr = [...linkedOfferteItems];
                              const comps = (arr[index].components || []).filter((_, i) => i !== ci);
                              arr[index] = { ...arr[index], components: comps };
                              setLinkedOfferteItems(arr);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => {
                          const arr = [...linkedOfferteItems];
                          const comps = [...(arr[index].components || []), { description: '', quantity: 0 }];
                          arr[index] = { ...arr[index], components: comps };
                          setLinkedOfferteItems(arr);
                        }}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Specificatie toevoegen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEditingInstallationFactuur(null)}>
              Annuleren
            </Button>
            <Button onClick={async () => {
              try {
                setSaving(true);
                // 1) Save the factuur itself
                await installationFactuurAPI.update(editingInstallationFactuur.id, {
                  factuur_number: editingInstallationFactuur?.factuur_number,
                  items: installationEditItems.map(it => ({
                    ...it,
                    quantity: parseDec(it.quantity ?? 1),
                    unit_price: parseDec(it.unit_price),
                    components: (it.components || []).map(c => ({ ...c, quantity: parseDec(c.quantity) })),
                  })),
                  factuur_date: editingInstallationFactuur?.factuur_date || null,
                  due_date: editingInstallationFactuur?.due_date || null,
                  vat_percentage: editingInstallationFactuur?.vat_percentage ?? 6,
                  installation_date: editingInstallationFactuur?.installation_date || null,
                  connection_date: editingInstallationFactuur?.connection_date || null,
                  inspection_date: editingInstallationFactuur?.inspection_date || null,
                  billing_address: (editingInstallationFactuur?.billing_address || '').trim(),
                  installation_address: (editingInstallationFactuur?.installation_address || '').trim(),
                  customer_name_override: (editingInstallationFactuur?.customer_name_override || '').trim(),
                  attn_override: (editingInstallationFactuur?.attn_override || '').trim(),
                  ...(editingInstallationFactuur?.use_manual_subtotal ? {
                    use_manual_subtotal: true,
                    manual_subtotal: parseDec(editingInstallationFactuur?.manual_subtotal),
                  } : {})
                });
                // 2) If a linked offerte was loaded, persist the products & specifications changes on the offerte
                if (linkedOfferteId) {
                  await installationOfferteAPI.update(linkedOfferteId, {
                    items: linkedOfferteItems.map(it => ({
                      product_id: it.product_id || null,
                      name: it.name,
                      description: it.description,
                      components: (it.components || []).map(c => ({ ...c, quantity: parseDec(c.quantity) })),
                      sub_products: it.sub_products || [],
                      quantity: it.quantity === '' || it.quantity === null || it.quantity === undefined ? 1 : parseDec(it.quantity),
                      unit_price: parseDec(it.unit_price),
                      unit: it.unit,
                      is_optional: it.is_optional || false,
                      show_price: it.show_price === true,
                    })),
                  });
                }
                toast.success('Factuur bijgewerkt');
                setEditingInstallationFactuur(null);
                setLinkedOfferteId(null);
                setLinkedOfferteItems([]);
                fetchData();
              } catch (error) {
                console.error(error);
                toast.error('Fout bij opslaan');
              } finally {
                setSaving(false);
              }
            }} disabled={saving}>
              {saving ? 'Opslaan...' : 'Bijwerken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Installation Factuur Dialog (without offerte) */}
      <Dialog open={manualFactuurDialogOpen} onOpenChange={setManualFactuurDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-install-factuur-dialog">
          <DialogHeader>
            <DialogTitle>Nieuwe Manuele Installatie Factuur</DialogTitle>
            <DialogDescription>
              Maak een installatie-factuur zonder gekoppelde offerte
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Factuur meta row 1: Nummer + Verberg prijzen toggle */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Factuurnummer</Label>
                <Input
                  value={manualFactuurForm.factuur_number}
                  onChange={(e) => setManualFactuurForm(prev => ({ ...prev, factuur_number: e.target.value }))}
                  placeholder="Automatisch gegenereerd"
                  data-testid="manual-factuur-number-input"
                />
                <p className="text-xs text-muted-foreground">Laat leeg voor automatische nummering</p>
              </div>
              <div className="space-y-2">
                <Label>Prijsweergave op PDF</Label>
                <div className="flex items-center gap-2 h-10">
                  <Button
                    type="button"
                    variant={manualFactuurForm.hide_unit_prices ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => setManualFactuurForm(prev => ({ ...prev, hide_unit_prices: false }))}
                    data-testid="manual-factuur-show-prices-btn"
                  >
                    <Eye className="w-4 h-4 mr-1" /> Eenheidsprijzen zichtbaar
                  </Button>
                  <Button
                    type="button"
                    variant={manualFactuurForm.hide_unit_prices ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setManualFactuurForm(prev => ({ ...prev, hide_unit_prices: true }))}
                    data-testid="manual-factuur-hide-prices-btn"
                  >
                    <EyeOff className="w-4 h-4 mr-1" /> Verbergen
                  </Button>
                </div>
              </div>
            </div>

            {/* Klant + Pand */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Klant *</Label>
                <SearchSelect
                  options={[...customers]
                    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'nl'))
                    .map(c => ({
                      value: c.id,
                      label: `${c.first_name} ${c.last_name}${c.company_name ? ` (${c.company_name})` : ''}`
                    }))}
                  value={manualFactuurForm.customer_id}
                  onChange={(v) => setManualFactuurForm(prev => ({ ...prev, customer_id: v, property_id: '' }))}
                  placeholder="Zoek een klant..."
                  data-testid="manual-factuur-customer-select"
                />
              </div>
              <div className="space-y-2">
                <Label>Adres *</Label>
                <Select
                  value={manualFactuurForm.property_id}
                  onValueChange={(v) => setManualFactuurForm(prev => ({ ...prev, property_id: v }))}
                  disabled={!manualFactuurForm.customer_id}
                >
                  <SelectTrigger data-testid="manual-factuur-property-select">
                    <SelectValue placeholder={manualFactuurForm.customer_id ? "Selecteer een adres" : "Kies eerst een klant"} />
                  </SelectTrigger>
                  <SelectContent>
                    {properties
                      .filter(p => p.customer_id === manualFactuurForm.customer_id)
                      .map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.street} {p.number}, {p.postal_code} {p.city}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Manuele klantnaam + T.a.v. (optioneel) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Klantnaam op factuur <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Input
                  value={manualFactuurForm.customer_name_override}
                  onChange={(e) => setManualFactuurForm(prev => ({ ...prev, customer_name_override: e.target.value }))}
                  placeholder="Leeg = naam van de geselecteerde klant"
                  data-testid="manual-factuur-name-override"
                />
              </div>
              <div className="space-y-2">
                <Label>T.a.v. <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Input
                  value={manualFactuurForm.attn_override}
                  onChange={(e) => setManualFactuurForm(prev => ({ ...prev, attn_override: e.target.value }))}
                  placeholder="bv. Jan Peeters"
                  data-testid="manual-factuur-attn-override"
                />
              </div>
            </div>

            {/* Facturatie- en projectadres (optioneel) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Facturatieadres <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Input
                  value={manualFactuurForm.billing_address}
                  onChange={(e) => setManualFactuurForm(prev => ({ ...prev, billing_address: e.target.value }))}
                  placeholder="bv. Nieuwstraat 12, 1000 Brussel"
                  data-testid="manual-factuur-billing-address"
                />
                <p className="text-xs text-muted-foreground">Leeg = facturatieadres van de klant of het geselecteerde adres</p>
              </div>
              <div className="space-y-2">
                <Label>Projectadres <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Input
                  value={manualFactuurForm.installation_address}
                  onChange={(e) => setManualFactuurForm(prev => ({ ...prev, installation_address: e.target.value }))}
                  placeholder="bv. Werfstraat 8, 9000 Gent"
                  data-testid="manual-factuur-project-address"
                />
                <p className="text-xs text-muted-foreground">Wordt als "Installatie-adres" op de PDF getoond</p>
              </div>
            </div>

            {/* Meta: taal / BTW / vervaldagen / referentie */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Taal</Label>
                <Select value={manualFactuurForm.language} onValueChange={(v) => setManualFactuurForm(prev => ({ ...prev, language: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">BTW %</Label>
                <Select
                  value={String(manualFactuurForm.vat_percentage)}
                  onValueChange={(v) => setManualFactuurForm(prev => ({ ...prev, vat_percentage: parseFloat(v) }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0%</SelectItem>
                    <SelectItem value="6">6%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="21">21%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Vervaldagen</Label>
                <Input
                  type="number"
                  min="0"
                  value={manualFactuurForm.due_days}
                  onChange={(e) => setManualFactuurForm(prev => ({ ...prev, due_days: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Referentie</Label>
                <Input
                  value={manualFactuurForm.reference}
                  onChange={(e) => setManualFactuurForm(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Optioneel"
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-base font-semibold">Producten & specificaties</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setProductSelectorOpen(true)}
                    data-testid="manual-factuur-add-product-btn"
                  >
                    <Package className="w-4 h-4 mr-1" /> Product toevoegen
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setManualFactuurForm(prev => ({
                      ...prev,
                      items: [...prev.items, { name: '', description: '', quantity: 1, unit_price: 0, unit: 'stuk', show_price: true, components: [] }]
                    }))}
                    data-testid="manual-factuur-add-item"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Aangepast item
                  </Button>
                </div>
              </div>

              {/* Items header - matches offerte layout */}
              {manualFactuurForm.items.length > 0 && (
                <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2">
                  <div className="col-span-4">Omschrijving</div>
                  <div className="col-span-2">Aantal / Eenheid</div>
                  <div className="col-span-2">Eenheidsprijs</div>
                  <div className="col-span-2 text-right">Totaal</div>
                  <div className="col-span-2 text-right">Acties</div>
                </div>
              )}

              {manualFactuurForm.items.map((item, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-3 bg-muted/20">
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-4">
                      <Input
                        value={item.name}
                        onChange={(e) => {
                          const arr = [...manualFactuurForm.items];
                          arr[index] = { ...arr[index], name: e.target.value };
                          setManualFactuurForm(prev => ({ ...prev, items: arr }));
                        }}
                        placeholder="Naam item"
                        data-testid={`manual-factuur-item-name-${index}`}
                      />
                      <Input
                        className="mt-2 text-sm"
                        value={item.description || ''}
                        onChange={(e) => {
                          const arr = [...manualFactuurForm.items];
                          arr[index] = { ...arr[index], description: e.target.value };
                          setManualFactuurForm(prev => ({ ...prev, items: arr }));
                        }}
                        placeholder="Beschrijving (optioneel)"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.quantity}
                        onChange={(e) => {
                          const arr = [...manualFactuurForm.items];
                          arr[index] = { ...arr[index], quantity: e.target.value };
                          setManualFactuurForm(prev => ({ ...prev, items: arr }));
                        }}
                        className="text-sm"
                        data-testid={`manual-factuur-item-qty-${index}`}
                      />
                      <Input
                        className="mt-1 text-xs"
                        value={item.unit || 'stuk'}
                        onChange={(e) => {
                          const arr = [...manualFactuurForm.items];
                          arr[index] = { ...arr[index], unit: e.target.value };
                          setManualFactuurForm(prev => ({ ...prev, items: arr }));
                        }}
                        placeholder="Eenheid"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.unit_price}
                        onChange={(e) => {
                          const arr = [...manualFactuurForm.items];
                          arr[index] = { ...arr[index], unit_price: e.target.value };
                          setManualFactuurForm(prev => ({ ...prev, items: arr }));
                        }}
                        className="text-sm"
                        data-testid={`manual-factuur-item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-2 text-right font-medium pt-2 text-sm">
                      €{(parseDec(item.quantity) * parseDec(item.unit_price)).toFixed(2)}
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={`h-7 px-2 ${item.show_price === true ? 'text-primary' : 'text-muted-foreground'}`}
                        onClick={() => {
                          const arr = [...manualFactuurForm.items];
                          arr[index] = { ...arr[index], show_price: !arr[index].show_price };
                          setManualFactuurForm(prev => ({ ...prev, items: arr }));
                        }}
                        data-testid={`manual-factuur-item-showprice-${index}`}
                        title={item.show_price === true ? 'Prijs zichtbaar op PDF' : 'Prijs verborgen op PDF'}
                      >
                        {item.show_price === true ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive h-7 w-7"
                        disabled={manualFactuurForm.items.length === 1}
                        onClick={() => setManualFactuurForm(prev => ({
                          ...prev,
                          items: prev.items.filter((_, i) => i !== index)
                        }))}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Components / specifications */}
                  <div className="pl-4 border-l-2 border-muted space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Specificaties:</p>
                    {(item.components || []).map((comp, ci) => (
                      <div key={ci} className="flex items-center gap-2">
                        <span className="text-muted-foreground">-</span>
                        <Input
                          className="flex-1 text-sm h-8"
                          value={comp.description || ''}
                          onChange={(e) => {
                            const arr = [...manualFactuurForm.items];
                            const comps = [...(arr[index].components || [])];
                            comps[ci] = { ...comps[ci], description: e.target.value };
                            arr[index] = { ...arr[index], components: comps };
                            setManualFactuurForm(prev => ({ ...prev, items: arr }));
                          }}
                          placeholder="Specificatie"
                        />
                          <Input
                            type="text"
                            inputMode="decimal"
                            className="w-16 text-sm h-8 text-center"
                            value={comp.quantity ?? 0}
                            onChange={(e) => {
                              const arr = [...manualFactuurForm.items];
                              const comps = [...(arr[index].components || [])];
                              comps[ci] = { ...comps[ci], quantity: e.target.value };
                              arr[index] = { ...arr[index], components: comps };
                              setManualFactuurForm(prev => ({ ...prev, items: arr }));
                            }}
                          />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => {
                            const arr = [...manualFactuurForm.items];
                            const comps = (arr[index].components || []).filter((_, i) => i !== ci);
                            arr[index] = { ...arr[index], components: comps };
                            setManualFactuurForm(prev => ({ ...prev, items: arr }));
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => {
                        const arr = [...manualFactuurForm.items];
                        const comps = [...(arr[index].components || []), { description: '', quantity: 0 }];
                        arr[index] = { ...arr[index], components: comps };
                        setManualFactuurForm(prev => ({ ...prev, items: arr }));
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Specificatie toevoegen
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notities</Label>
              <Textarea
                value={manualFactuurForm.notes}
                onChange={(e) => setManualFactuurForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optionele notities voor deze factuur"
                rows={3}
              />
            </div>

            {/* Totals - with manual subtotal override like offerte */}
            <div className="border-t pt-4 space-y-2">
              {(() => {
                const computedSubtotal = manualFactuurForm.items.reduce(
                  (s, it) => s + parseDec(it.quantity) * parseDec(it.unit_price), 0
                );
                const useManual = manualFactuurForm.use_manual_subtotal;
                const subtotal = useManual ? parseDec(manualFactuurForm.manual_subtotal) : computedSubtotal;
                const vat = subtotal * (manualFactuurForm.vat_percentage / 100);
                return (
                  <>
                    {/* Manual subtotal toggle */}
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Checkbox
                        id="manual-factuur-use-manual-subtotal"
                        checked={useManual}
                        onCheckedChange={(checked) => {
                          setManualFactuurForm(prev => ({
                            ...prev,
                            use_manual_subtotal: !!checked,
                            manual_subtotal: checked ? (prev.manual_subtotal || computedSubtotal) : prev.manual_subtotal,
                          }));
                        }}
                        data-testid="manual-factuur-use-manual-subtotal-checkbox"
                      />
                      <Label
                        htmlFor="manual-factuur-use-manual-subtotal"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Handmatige prijs gebruiken
                      </Label>
                    </div>

                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Subtotaal excl. BTW</span>
                      {useManual ? (
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">€</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={manualFactuurForm.manual_subtotal}
                            onChange={(e) => setManualFactuurForm(prev => ({
                              ...prev,
                              manual_subtotal: e.target.value,
                            }))}
                            className="w-32 h-8 text-right"
                            min="0"
                            data-testid="manual-factuur-manual-subtotal-input"
                          />
                        </div>
                      ) : (
                        <span>€{subtotal.toFixed(2)}</span>
                      )}
                    </div>
                    {useManual && (
                      <p className="text-xs text-muted-foreground text-right">
                        Berekende prijs uit items: €{computedSubtotal.toFixed(2)}
                      </p>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">BTW ({manualFactuurForm.vat_percentage}%)</span>
                      <span>€{vat.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base pt-1 border-t">
                      <span>Totaal incl. BTW</span>
                      <span>€{(subtotal + vat).toFixed(2)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setManualFactuurDialogOpen(false)}>
              Annuleren
            </Button>
            <Button
              disabled={
                manualFactuurSaving ||
                !manualFactuurForm.customer_id ||
                !manualFactuurForm.property_id ||
                (
                  !manualFactuurForm.use_manual_subtotal &&
                  (manualFactuurForm.items.length === 0 || manualFactuurForm.items.every(it => !it.name))
                )
              }
              onClick={async () => {
                try {
                  setManualFactuurSaving(true);
                  const computedSubtotal = manualFactuurForm.items.reduce((s, it) => s + parseDec(it.quantity) * parseDec(it.unit_price), 0);
                  const useManual = manualFactuurForm.use_manual_subtotal;
                  const subtotal = useManual ? parseDec(manualFactuurForm.manual_subtotal) : computedSubtotal;
                  const vat_amount = subtotal * (manualFactuurForm.vat_percentage / 100);
                  const total_incl_vat = subtotal + vat_amount;
                  const payload = {
                    customer_id: manualFactuurForm.customer_id,
                    property_id: manualFactuurForm.property_id,
                    offerte_id: null,
                    language: manualFactuurForm.language,
                    vat_percentage: manualFactuurForm.vat_percentage,
                    due_days: manualFactuurForm.due_days,
                    subtotal,
                    vat_amount,
                    total_incl_vat,
                    hide_unit_prices: manualFactuurForm.hide_unit_prices,
                    use_manual_subtotal: useManual,
                    manual_subtotal: useManual ? subtotal : null,
                    notes: manualFactuurForm.notes || null,
                    reference: manualFactuurForm.reference || null,
                    billing_address: manualFactuurForm.billing_address?.trim() || null,
                    installation_address: manualFactuurForm.installation_address?.trim() || null,
                    customer_name_override: manualFactuurForm.customer_name_override?.trim() || null,
                    attn_override: manualFactuurForm.attn_override?.trim() || null,
                    items: manualFactuurForm.items
                      .filter(it => it.name)
                      .map(it => ({
                        name: it.name,
                        description: it.description || null,
                        quantity: it.quantity === '' || it.quantity === null || it.quantity === undefined ? 1 : parseDec(it.quantity),
                        unit_price: parseDec(it.unit_price),
                        unit: it.unit || 'stuk',
                        show_price: it.show_price !== false,
                        components: (it.components || []).filter(c => c.description).map(c => ({ ...c, quantity: parseDec(c.quantity) })),
                      })),
                    // Force single-invoice mode (100% due) by using explicit single term
                    payment_terms: [
                      { percentage: 100, due_days: manualFactuurForm.due_days, description: 'Volledige factuur' }
                    ],
                  };
                  const createResp = await installationFactuurAPI.create(payload);
                  // If user provided a custom factuur_number, update it via PUT (create endpoint auto-generates)
                  if (manualFactuurForm.factuur_number && manualFactuurForm.factuur_number.trim() && createResp?.data?.id) {
                    try {
                      await installationFactuurAPI.update(createResp.data.id, {
                        factuur_number: manualFactuurForm.factuur_number.trim(),
                      });
                    } catch (numErr) {
                      console.warn('Kon factuurnummer niet aanpassen:', numErr);
                      toast.warning('Factuur is aangemaakt, maar het factuurnummer kon niet gewijzigd worden');
                    }
                  }
                  toast.success('Manuele factuur aangemaakt');
                  setManualFactuurDialogOpen(false);
                  fetchData();
                } catch (error) {
                  console.error(error);
                  toast.error('Kon factuur niet aanmaken: ' + (error.response?.data?.detail || error.message));
                } finally {
                  setManualFactuurSaving(false);
                }
              }}
              data-testid="manual-factuur-save-btn"
            >
              {manualFactuurSaving ? 'Aanmaken...' : 'Factuur aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
        <InstallationProductSelector
          open={productSelectorOpen}
          onOpenChange={setProductSelectorOpen}
          products={installationProducts}
          onConfirm={(picked) => {
            setManualFactuurForm(prev => {
              // If the current single default empty row is still empty, replace it; otherwise append
              const cleanExisting = prev.items.filter(
                (it, idx) => !(idx === 0 && !it.name && (!it.quantity || it.quantity === 1) && (!it.unit_price || it.unit_price === 0) && (!it.components || it.components.length === 0) && prev.items.length === 1)
              );
              const newItems = picked.map(p => ({
                product_id: p.id,
                name: p.name,
                description: p.description || '',
                quantity: 1,
                unit_price: p.unit_price || 0,
                unit: p.unit || 'stuk',
                show_price: true,
                components: (p.components || []).map(c => ({
                  description: c.description || '',
                  quantity: c.quantity ?? 0,
                })),
              }));
              return { ...prev, items: [...cleanExisting, ...newItems] };
            });
          }}
          title="Producten Toevoegen aan Factuur"
          description="Selecteer één of meerdere producten uit de catalogus"
        />
      </Dialog>

      {/* Email Queue Dialog for Installation Facturen */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email verzenden - {emailDialogFactuur?.factuur_number}
            </DialogTitle>
            <DialogDescription>
              Controleer de email en kies hoe u wilt versturen
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Email Preview */}
            {emailPreviewLoading ? (
              <div className="p-4 rounded-lg border bg-muted/20 text-center">
                <span className="text-muted-foreground">Email preview laden...</span>
              </div>
            ) : emailPreview ? (
              <div className="space-y-3">
                <div className="p-3 rounded-lg border bg-muted/20 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Aan:</span>
                    <span>{emailPreview.to_name} &lt;{emailPreview.to_email}&gt;</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Onderwerp:</span>
                    <span className="font-medium">{emailPreview.subject}</span>
                  </div>
                  {emailPreview.term_description && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Type:</span>
                      <span>{emailPreview.term_description}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Bedrag:</span>
                    <span className="font-semibold">{emailPreview.total_amount}</span>
                  </div>
                </div>
                
                {/* Email Body Preview */}
                <div className="space-y-2">
                  <Label className="text-sm">Email inhoud (template)</Label>
                  <div 
                    className="p-4 rounded-lg border bg-white text-sm max-h-[200px] overflow-y-auto prose prose-sm"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(emailPreview.body_html) }}
                  />
                </div>
                
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  PDF factuur wordt als bijlage toegevoegd
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-muted/30 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Aan:</span>
                  <span>{emailDialogFactuur?.customer_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Bedrag:</span>
                  <span className="font-semibold">€{emailDialogFactuur?.total_incl_vat?.toFixed(2)}</span>
                </div>
              </div>
            )}
            
            {/* CC Selector */}
            <CcSelector
              selectedEmails={emailDialogCcEmails}
              onChange={setEmailDialogCcEmails}
              defaultEnabled={true}
            />
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} disabled={emailDialogSending}>
              Annuleren
            </Button>
            <Button 
              variant="secondary"
              onClick={handleQueueInstallationFactuurEmail}
              disabled={emailDialogSending}
            >
              <Inbox className="w-4 h-4 mr-2" />
              {emailDialogSending ? 'Bezig...' : 'Mail klaarzetten'}
            </Button>
            <Button 
              onClick={handleSendInstallationFactuurEmailDirect}
              disabled={emailDialogSending}
            >
              <Send className="w-4 h-4 mr-2" />
              {emailDialogSending ? 'Bezig...' : 'Direct verzenden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Facturen;
